<template>
  <div class="flex flex-col h-screen bg-gray-100">
    <nav-menu />

    <!-- ==================== HEADER BAR ==================== -->
    <div class="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
      <div class="flex items-center px-4 py-3">
        <!-- Mobile hamburger -->
        <button @click="showMobileProjects = true" class="lg:hidden mr-3 w-9 h-9 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100">
          <i class="fa-solid fa-bars"></i>
        </button>

        <!-- Back button (when project selected) -->
        <button v-if="selectedProject" @click="goBackToDashboard()" class="mr-2 w-9 h-9 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition">
          <i class="fa-solid fa-arrow-left"></i>
        </button>

        <h1 class="text-lg font-bold text-gray-900 mr-4 flex-shrink-0">Projects</h1>

        <!-- Search (desktop) -->
        <div class="hidden sm:flex items-center bg-gray-100 rounded-lg px-3 py-2 mr-4 flex-1 max-w-xs">
          <i class="fa-solid fa-search text-gray-400 mr-2 text-xs"></i>
          <input v-model="projectSearch" type="text" placeholder="Search projects..." class="bg-transparent outline-none text-sm w-full" />
        </div>

        <div class="ml-auto flex items-center space-x-2">
          <!-- Team leader tester filter -->
          <select v-if="isTeamLeader" v-model="teamLeaderFilter" class="text-sm border border-gray-200 rounded-lg px-3 h-9 outline-none bg-white text-gray-700">
            <option value="all">All My Jobs</option>
            <option value="unassigned">Unassigned</option>
            <option v-for="m in teamMembers" :key="m.id" :value="m.id">{{ m.name }}</option>
          </select>
          <button v-if="!isTeamLeader" @click="openAddProject()" class="hidden sm:flex items-center text-gray-600 hover:text-gray-800 px-3 py-2 rounded-lg text-sm hover:bg-gray-100 border border-gray-200">
            <i class="fa-solid fa-folder-plus mr-2"></i> New Project
          </button>
          <button v-if="!isTeamLeader" @click="openAddJobModal()" class="hidden sm:flex items-center bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700">
            <i class="fa-solid fa-plus mr-2"></i> New Job
          </button>
          <div v-if="!isTeamLeader" class="relative">
            <button @click="showSettingsMenu = !showSettingsMenu" class="flex items-center text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg text-sm hover:bg-gray-100">
              <i class="fa-solid fa-gear"></i>
            </button>
            <div v-if="showSettingsMenu" class="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-30 py-1">
              <button @click="switchToLegacy(); showSettingsMenu = false" class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                <i class="fa-solid fa-arrow-rotate-left mr-2 text-gray-400 w-4"></i>Legacy View
              </button>
              <button @click="openAutoArchiveModal(); showSettingsMenu = false" class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                <i class="fa-solid fa-box-archive mr-2 text-gray-400 w-4"></i>Auto Archive Jobs
              </button>
            </div>
            <div v-if="showSettingsMenu" class="fixed inset-0 z-20" @click="showSettingsMenu = false"></div>
          </div>
        </div>
      </div>

      <!-- Mobile search -->
      <div class="sm:hidden px-4 pb-2">
        <div class="flex items-center bg-gray-100 rounded-lg px-3 py-2">
          <i class="fa-solid fa-search text-gray-400 mr-2 text-xs"></i>
          <input v-model="projectSearch" type="text" placeholder="Search projects..." class="bg-transparent outline-none text-sm w-full" />
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex-1 flex items-center justify-center">
      <i class="fa-solid fa-spinner fa-spin text-3xl text-gray-400"></i>
    </div>

    <!-- ==================== MAIN CONTENT ==================== -->
    <div v-else class="flex-1 overflow-hidden">

      <!-- ========== PROJECTS & JOBS VIEW ========== -->
      <div class="h-full flex">

        <!-- LEFT SIDEBAR: Projects (Alphabetical Accordion) -->
        <div class="hidden lg:flex flex-col w-72 bg-white border-r border-gray-200 flex-shrink-0">
          <div class="flex items-center justify-between px-3 py-2 border-b border-gray-100">
            <div class="flex items-center">
              <button @click="projectTab = 'current'" :class="projectTab === 'current' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'" class="px-3 py-1 rounded-md text-xs font-medium mr-1">Current</button>
              <button @click="projectTab = 'archived'" :class="projectTab === 'archived' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'" class="px-3 py-1 rounded-md text-xs font-medium">Archived</button>
            </div>
            <button @click="openAddProject()" class="text-indigo-600 hover:text-indigo-800 p-1" title="New Project">
              <i class="fa-solid fa-folder-plus text-sm"></i>
            </button>
          </div>
          <!-- Search field checkboxes -->
          <div class="flex items-center gap-3 px-3 py-2 border-b border-gray-100 text-xs text-gray-500">
            <label class="flex items-center gap-1 cursor-pointer"><input type="checkbox" v-model="searchFields.projectName" class="w-3 h-3 rounded" /> Name</label>
            <label class="flex items-center gap-1 cursor-pointer"><input type="checkbox" v-model="searchFields.client" class="w-3 h-3 rounded" /> Client</label>
            <label class="flex items-center gap-1 cursor-pointer"><input type="checkbox" v-model="searchFields.address" class="w-3 h-3 rounded" /> Address</label>
          </div>
          <div class="flex-1 overflow-y-auto">
            <div v-if="filteredProjects.length === 0" class="p-4 text-center text-gray-400 text-sm">No projects found</div>
            <div
              v-for="project in filteredProjects"
              :key="project.projectId"
              @click="selectProject(project)"
              :class="[
                'px-4 py-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition',
                selectedProjectId == project.projectId ? 'bg-indigo-50 border-l-4 border-l-indigo-600' : ''
              ]"
            >
              <div class="flex items-center justify-between">
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-semibold text-gray-900 truncate">{{ project.projectName || 'Untitled' }}</p>
                  <p class="text-xs text-gray-500 truncate">{{ project.company }}</p>
                </div>
                <span class="ml-2 flex-shrink-0 text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">{{ getJobCount(project) }}</span>
              </div>
              <div v-if="project.formattedAddress" class="text-xs text-gray-400 truncate mt-1">
                <i class="fa-solid fa-location-dot mr-1"></i>{{ project.formattedAddress }}
              </div>
            </div>
          </div>
        </div>

        <!-- CENTER PANEL (2-column layout: sidebar + this) -->
        <div class="flex-1 overflow-hidden flex flex-col">
          <!-- No project selected — Mini Dashboard -->
          <div v-if="!selectedProject" class="flex-1 overflow-y-auto p-4 lg:p-6">
            <div class="mb-6">
              <h2 class="text-xl font-bold text-gray-900 mb-1">Jobs Dashboard</h2>
              <p class="text-sm text-gray-500">Select a project from the sidebar or create a new one</p>
              <button @click="showMobileProjects = true" class="lg:hidden mt-2 text-indigo-600 text-sm hover:underline">Browse Projects</button>
            </div>

            <!-- KPI Cards -->
            <div class="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div class="bg-white rounded-xl border border-gray-200 p-4">
                <p class="text-xs text-gray-500 uppercase font-medium mb-1">Open Projects</p>
                <p class="text-2xl font-bold text-gray-900">{{ kpiStats.openProjects }}</p>
              </div>
              <div class="bg-white rounded-xl border border-gray-200 p-4">
                <p class="text-xs text-gray-500 uppercase font-medium mb-1">Closed Projects</p>
                <p class="text-2xl font-bold text-gray-900">{{ kpiStats.closedProjects }}</p>
              </div>
              <div class="bg-white rounded-xl border border-gray-200 p-4">
                <p class="text-xs text-gray-500 uppercase font-medium mb-1">Total Jobs</p>
                <p class="text-2xl font-bold text-gray-900">{{ kpiStats.total }}</p>
              </div>
            </div>

            <h3 class="text-sm font-bold text-gray-700 uppercase mb-3">Jobs by Status</h3>
            <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div class="bg-white rounded-xl border border-yellow-200 p-4">
                <div class="flex items-center mb-2">
                  <span class="w-3 h-3 rounded-full bg-yellow-400 mr-2"></span>
                  <p class="text-xs text-yellow-700 font-medium">ON-CALL</p>
                </div>
                <p class="text-2xl font-bold text-gray-900">{{ kpiStats.onCall }}</p>
              </div>
              <div class="bg-white rounded-xl border border-green-200 p-4">
                <div class="flex items-center mb-2">
                  <span class="w-3 h-3 rounded-full bg-green-500 mr-2"></span>
                  <p class="text-xs text-green-700 font-medium">FIRM</p>
                </div>
                <p class="text-2xl font-bold text-gray-900">{{ kpiStats.firm }}</p>
              </div>
              <div class="bg-white rounded-xl border border-red-200 p-4">
                <div class="flex items-center mb-2">
                  <span class="w-3 h-3 rounded-full bg-red-500 mr-2"></span>
                  <p class="text-xs text-red-700 font-medium">CANCELLED</p>
                </div>
                <p class="text-2xl font-bold text-gray-900">{{ kpiStats.cancelled }}</p>
              </div>
              <div class="bg-white rounded-xl border border-green-300 p-4">
                <div class="flex items-center mb-2">
                  <span class="w-3 h-3 rounded-full bg-green-600 mr-2"></span>
                  <p class="text-xs text-green-800 font-medium">COMPLETED</p>
                </div>
                <p class="text-2xl font-bold text-gray-900">{{ kpiStats.completed }}</p>
              </div>
            </div>
          </div>

          <!-- Project selected — Header + Tabbed content area -->
          <template v-else>
            <!-- PROJECT HEADER (always visible) -->
            <div class="bg-white border-b border-gray-200 px-4 lg:px-6 py-4 flex-shrink-0">
              <div class="flex items-start justify-between mb-3">
                <div class="min-w-0">
                  <div class="flex items-center flex-wrap gap-2">
                    <h2 class="text-lg font-bold text-gray-900 truncate">{{ selectedProject.projectName }}</h2>
                    <span class="text-xs bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">#{{ selectedProject.projectId }}</span>
                    <span v-if="selectedProject.closed" class="text-xs bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">Closed</span>
                  </div>
                  <p class="text-sm text-gray-500 mt-0.5">{{ selectedProject.company }}</p>
                </div>
                <div class="flex items-center space-x-1 flex-shrink-0 ml-2">
                  <button v-if="!selectedProject.closed" @click="promptCloseProject()" class="px-3 py-1.5 rounded-lg text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-colors" title="Close project">
                    <i class="fa-solid fa-lock-open mr-1"></i>Open
                  </button>
                  <button v-else @click="promptReopenProject()" class="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors" title="Reopen project">
                    <i class="fa-solid fa-lock mr-1"></i>Closed
                  </button>
                  <button @click="duplicateProject()" class="text-gray-400 hover:text-indigo-600 p-2 rounded-lg hover:bg-indigo-50 transition-colors" title="Duplicate project">
                    <i class="fa-solid fa-copy"></i>
                  </button>
                  <button @click="openEditProject()" class="text-gray-400 hover:text-indigo-600 p-2 rounded-lg hover:bg-indigo-50 transition-colors" title="Edit project">
                    <i class="fa-solid fa-pen-to-square"></i>
                  </button>
                </div>
              </div>

              <!-- Project details grid -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm mb-3">
                <!-- Primary contact -->
                <div v-if="selectedProject.contact" class="flex items-center text-gray-600 min-w-0">
                  <i class="fa-solid fa-user w-4 text-gray-400 flex-shrink-0 text-xs"></i>
                  <span class="ml-2 truncate">{{ selectedProject.contact }}</span>
                </div>
                <div v-if="selectedProject.phone" class="flex items-center min-w-0">
                  <i class="fa-solid fa-phone w-4 text-gray-400 flex-shrink-0 text-xs"></i>
                  <a :href="'tel:' + selectedProject.phone" class="ml-2 text-indigo-600 hover:underline truncate">{{ selectedProject.phone }}</a>
                </div>
                <div v-if="selectedProject.email" class="flex items-center min-w-0">
                  <i class="fa-solid fa-envelope w-4 text-gray-400 flex-shrink-0 text-xs"></i>
                  <a :href="'mailto:' + selectedProject.email" class="ml-2 text-indigo-600 hover:underline truncate">{{ selectedProject.email }}</a>
                </div>
                <!-- Secondary contact -->
                <div v-if="selectedProject.secondaryContact" class="flex items-center text-gray-600 min-w-0">
                  <i class="fa-solid fa-user-plus w-4 text-gray-400 flex-shrink-0 text-xs"></i>
                  <span class="ml-2 truncate">{{ selectedProject.secondaryContact }}</span>
                </div>
                <div v-if="selectedProject.secondaryPhone" class="flex items-center min-w-0">
                  <i class="fa-solid fa-phone-flip w-4 text-gray-400 flex-shrink-0 text-xs"></i>
                  <a :href="'tel:' + selectedProject.secondaryPhone" class="ml-2 text-indigo-600 hover:underline truncate">{{ selectedProject.secondaryPhone }}</a>
                </div>
                <div v-if="selectedProject.secondaryEmail" class="flex items-center min-w-0">
                  <i class="fa-solid fa-at w-4 text-gray-400 flex-shrink-0 text-xs"></i>
                  <a :href="'mailto:' + selectedProject.secondaryEmail" class="ml-2 text-indigo-600 hover:underline truncate">{{ selectedProject.secondaryEmail }}</a>
                </div>
                <!-- Address -->
                <div v-if="selectedProject.formattedAddress" class="flex items-center min-w-0 sm:col-span-2">
                  <i class="fa-solid fa-location-dot w-4 text-gray-400 flex-shrink-0 text-xs"></i>
                  <a :href="'https://maps.google.com/?q=' + encodeURIComponent(selectedProject.formattedAddress)" target="_blank" class="ml-2 text-indigo-600 hover:underline truncate">{{ selectedProject.formattedAddress }}</a>
                </div>
                <!-- PO, Region, Call Frequency -->
                <div v-if="selectedProject.poNumber" class="flex items-center text-gray-600 min-w-0">
                  <i class="fa-solid fa-file-invoice w-4 text-gray-400 flex-shrink-0 text-xs"></i>
                  <span class="ml-2 truncate">PO: {{ selectedProject.poNumber }}</span>
                </div>
                <div v-if="selectedProject.region" class="flex items-center text-gray-600 min-w-0">
                  <i class="fa-solid fa-map w-4 text-gray-400 flex-shrink-0 text-xs"></i>
                  <span class="ml-2 truncate">{{ selectedProject.region }}</span>
                </div>
                <div v-if="selectedProject.callFrequency" class="flex items-center text-gray-600 min-w-0">
                  <i class="fa-solid fa-phone-volume w-4 text-gray-400 flex-shrink-0 text-xs"></i>
                  <span class="ml-2 truncate capitalize">{{ selectedProject.callFrequency }}</span>
                </div>
                <div v-if="selectedProject.teamLeaders && selectedProject.teamLeaders.length" class="flex items-start min-w-0 sm:col-span-2">
                  <i class="fa-solid fa-star w-4 text-gray-400 flex-shrink-0 text-xs mt-0.5"></i>
                  <div class="ml-2 flex flex-wrap gap-1">
                    <span v-for="tl in selectedProject.teamLeaders" :key="tl.id" class="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700 text-xs font-medium">{{ tl.name }}</span>
                  </div>
                </div>
                <!-- Dates -->
                <div v-if="selectedProject.estimatedStartDate || selectedProject.estimatedEndDate" class="flex items-center text-gray-500 min-w-0">
                  <i class="fa-regular fa-calendar w-4 text-gray-400 flex-shrink-0 text-xs"></i>
                  <span class="ml-2 truncate">
                    <span v-if="selectedProject.estimatedStartDate">{{ formatTimestamp(selectedProject.estimatedStartDate) }}</span>
                    <span v-if="selectedProject.estimatedStartDate && selectedProject.estimatedEndDate"> &mdash; </span>
                    <span v-if="selectedProject.estimatedEndDate">{{ formatTimestamp(selectedProject.estimatedEndDate) }}</span>
                  </span>
                </div>
              </div>

              <!-- Sub-navigation tabs -->
              <div class="flex items-center border-b -mb-4 -mx-4 lg:-mx-6 px-4 lg:px-6">
                <button @click="contextTab = 'jobs'" :class="contextTab === 'jobs' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'" class="mr-6 pb-3 border-b-2 text-sm font-medium transition-colors whitespace-nowrap">
                  <i class="fa-solid fa-briefcase mr-1.5"></i>Jobs <span class="text-xs text-gray-400 ml-1">({{ currentJobs.length }})</span>
                </button>
                <button @click="contextTab = 'map'" :class="contextTab === 'map' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'" class="mr-6 pb-3 border-b-2 text-sm font-medium transition-colors whitespace-nowrap">
                  <i class="fa-solid fa-map-location-dot mr-1.5"></i>Map
                </button>
                <button @click="contextTab = 'sms'" :class="contextTab === 'sms' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'" class="mr-6 pb-3 border-b-2 text-sm font-medium transition-colors whitespace-nowrap">
                  <i class="fa-solid fa-comment-sms mr-1.5"></i>SMS
                </button>
                <button v-if="!isTeamLeader" @click="openAddJobModal()" class="ml-auto pb-3 flex items-center text-xs text-indigo-600 font-medium hover:text-indigo-800 whitespace-nowrap">
                  <i class="fa-solid fa-plus mr-1"></i>Add Job
                </button>
              </div>
            </div>

            <!-- JOBS TAB -->
            <div v-show="contextTab === 'jobs'" class="flex-1 overflow-y-auto p-4 lg:p-6">
              <div class="max-w-4xl">

                <!-- Inline Notes (collapsible) -->
                <div v-if="selectedProject.notes || editingProjectNotes" class="bg-gray-50 rounded-xl border border-gray-200 p-4 mb-4">
                  <div class="flex items-center justify-between mb-1">
                    <label class="text-xs font-medium text-gray-500 uppercase">Project Notes</label>
                    <button v-if="!editingProjectNotes" @click="startEditProjectNotes()" class="text-xs text-indigo-600 hover:underline">Edit</button>
                    <div v-else class="flex items-center space-x-2">
                      <button @click="editingProjectNotes = false" class="text-xs text-gray-400 hover:underline">Cancel</button>
                      <button @click="saveProjectNotes()" class="text-xs text-green-600 hover:underline font-medium">Save</button>
                    </div>
                  </div>
                  <p v-if="!editingProjectNotes" class="text-sm text-gray-600 whitespace-pre-wrap">{{ selectedProject.notes }}</p>
                  <textarea v-else v-model="projectNotesText" class="w-full text-sm border border-gray-200 rounded-lg p-2 outline-none focus:border-indigo-400 bg-white" rows="3"></textarea>
                </div>

                <!-- ACTIVE JOBS -->
                <div class="mb-4">
                  <div class="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h3 class="text-sm font-bold text-gray-700 uppercase">Active Jobs</h3>
                    <div class="flex items-center space-x-2">
                      <!-- Date range filter -->
                      <div class="flex items-center space-x-1">
                        <flat-pickr v-model="jobFilterFrom" :config="dateOnlyConfig" placeholder="From" class="w-28 text-xs bg-gray-50 border border-gray-200 rounded-lg px-2 h-8 outline-none" />
                        <span class="text-xs text-gray-400">to</span>
                        <flat-pickr v-model="jobFilterTo" :config="dateOnlyConfig" placeholder="To" class="w-28 text-xs bg-gray-50 border border-gray-200 rounded-lg px-2 h-8 outline-none" />
                        <button v-if="jobFilterFrom || jobFilterTo" @click="jobFilterFrom = ''; jobFilterTo = ''" class="text-xs text-gray-400 hover:text-gray-600 p-1" title="Clear filter"><i class="fa-solid fa-xmark"></i></button>
                      </div>
                      <!-- Grid / List toggle -->
                      <div class="flex items-center bg-gray-100 rounded-lg p-0.5">
                        <button @click="jobViewMode = 'list'" :class="jobViewMode === 'list' ? 'bg-white shadow text-gray-900' : 'text-gray-400'" class="p-1.5 rounded-md transition" title="List view"><i class="fa-solid fa-list text-xs"></i></button>
                        <button @click="jobViewMode = 'grid'" :class="jobViewMode === 'grid' ? 'bg-white shadow text-gray-900' : 'text-gray-400'" class="p-1.5 rounded-md transition" title="Grid view"><i class="fa-solid fa-table-cells text-xs"></i></button>
                      </div>
                    </div>
                  </div>

                  <div v-if="filteredCurrentJobs.length === 0" class="bg-white rounded-xl border border-dashed border-gray-300 p-8 text-center text-gray-400">
                    <i class="fa-solid fa-briefcase text-3xl mb-2"></i>
                    <p>No active jobs{{ (jobFilterFrom || jobFilterTo) ? ' matching filter' : ' for this project' }}</p>
                  </div>

                  <div :class="jobViewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3' : 'space-y-3'">
                    <div
                      v-for="job in filteredCurrentJobs"
                      :key="job.id || job._tempId"
                      @click="!job._editing && selectJob(job)"
                      :class="[
                        'rounded-xl border p-4 transition',
                        job._editing ? '' : 'cursor-pointer',
                        job.status === 'COMPLETED' ? 'bg-green-100 border-green-300' : job.status === 'CANCELLED' ? 'bg-red-100 border-red-300' : 'bg-white border-gray-200 hover:border-gray-300',
                        selectedJobId === job.id && !job._editing ? 'ring-1 ring-indigo-200' : ''
                      ]"
                    >
                      <!-- VIEW MODE -->
                      <template v-if="!job._editing">
                        <!-- Header row: time + status + actions -->
                        <div class="flex items-start justify-between mb-3">
                          <div class="flex items-baseline flex-wrap gap-x-3 gap-y-1">
                            <span v-if="job.jobNumber" class="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">#{{ job.jobNumber }}</span>
                            <span class="text-xl font-bold text-gray-900 leading-none">{{ formatJobTime(job) }}</span>
                            <span class="text-sm text-gray-400">{{ formatJobDateShort(job) }}</span>
                            <span :class="statusBadgeClass(job.status)" class="text-xs font-medium px-2 py-0.5 rounded-full">{{ job.status }}</span>
                            <span v-if="isJobToday(job)" class="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Today</span>
                            <span v-else-if="isJobTomorrow(job)" class="text-xs font-semibold px-2 py-0.5 rounded-full bg-pink-100 text-pink-700">Tomorrow</span>
                          </div>
                          <div class="flex items-center space-x-1 flex-shrink-0" @click.stop>
                            <div v-if="getTestReportCount(job.id)" class="relative">
                              <button @click="job._showTestReports = !job._showTestReports" class="p-1.5 rounded-lg text-amber-500 hover:text-amber-600 hover:bg-amber-50" title="Test Reports">
                                <i class="fa-solid fa-paperclip text-sm"></i>
                                <span class="text-xs ml-0.5">{{ getTestReportCount(job.id) }}</span>
                              </button>
                              <div v-if="job._showTestReports" class="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-50 w-64">
                                <p class="text-xs font-semibold text-gray-500 uppercase mb-2">Test Reports</p>
                                <div v-for="tr in getTestReportFiles(job.id)" :key="tr.id" class="mb-2 last:mb-0">
                                  <p class="text-xs text-gray-700 font-medium">{{ tr.subject }}</p>
                                  <div v-for="(att, ai) in tr.attachments" :key="ai">
                                    <a :href="att.downloadUrl" target="_blank" class="text-xs text-indigo-600 hover:underline"><i class="fa-solid fa-file-pdf text-red-500 mr-1"></i>{{ att.filename }}</a>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <button @click="openLinkModal(job)" class="p-1.5 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50" title="Client Link"><i class="fa-solid fa-link text-sm"></i></button>
                            <button v-if="!isTeamLeader" @click="openSmsModal(job)" class="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50" title="Send SMS"><i class="fa-solid fa-comment-sms text-sm"></i></button>
                            <button v-if="!isTeamLeader" @click="startEditJob(job)" class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100" title="Edit"><i class="fa-solid fa-pen text-sm"></i></button>
                            <button v-if="!isTeamLeader" @click="duplicateJob(job)" class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100" title="Duplicate"><i class="fa-solid fa-copy text-sm"></i></button>
                            <button v-if="!isTeamLeader" @click="confirmDeleteJob(job)" class="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" title="Delete"><i class="fa-solid fa-trash text-sm"></i></button>
                          </div>
                        </div>

                        <!-- Job details row: mix, meters, pour -->
                        <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 mb-3">
                          <custom-fields-display v-if="customFieldsEnabled" :job="job" :fields="jobCardFields(job)" />
                          <template v-else>
                            <span v-if="job.mixCode" class="flex items-center"><i class="fa-solid fa-flask text-gray-400 mr-1.5 text-xs"></i><span class="font-medium">{{ job.mixCode }}</span></span>
                            <span v-if="job.meters" class="flex items-center"><i class="fa-solid fa-ruler text-gray-400 mr-1.5 text-xs"></i>{{ job.meters }}m&sup3;</span>
                          </template>
                          <span v-if="job.duration" class="flex items-center"><i class="fa-regular fa-clock text-gray-400 mr-1.5 text-xs"></i>{{ job.duration }}min</span>
                          <span v-if="!customFieldsEnabled && job.pourLocation" class="flex items-center text-gray-500"><i class="fa-solid fa-location-crosshairs text-gray-400 mr-1.5 text-xs"></i>{{ job.pourLocation }}</span>
                        </div>

                        <!-- Requirements -->
                        <div v-if="job.requirements" class="mb-3 text-xs text-gray-500 bg-blue-50 rounded-lg p-2.5">
                          <span class="font-semibold text-blue-700 uppercase text-[10px] tracking-wide">Requirements</span>
                          <p class="mt-0.5 text-blue-800">{{ job.requirements }}</p>
                        </div>

                        <!-- Tester card (inline in job) -->
                        <div v-if="job.contractor && job.contractor.name" class="bg-gray-50 rounded-lg p-3 mb-3">
                          <div class="flex items-center justify-between">
                            <div class="flex items-center">
                              <div :class="['w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold', testerLocations[job.contractor.id] ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500']">
                                {{ initials(job.contractor.name) }}
                              </div>
                              <div class="ml-2.5">
                                <div class="flex items-center space-x-2">
                                  <span class="text-sm font-medium text-gray-800">{{ job.contractor.name }}</span>
                                  <span v-if="testerLocations[job.contractor.id]" class="flex items-center text-xs text-green-600">
                                    <span class="w-1.5 h-1.5 rounded-full bg-green-500 mr-1 animate-pulse"></span>Online
                                  </span>
                                  <span v-else class="text-xs text-gray-400">Offline</span>
                                </div>
                                <div class="flex items-center mt-0.5">
                                  <a v-if="getTesterPhone(job.contractor)" :href="'tel:' + getTesterPhone(job.contractor)" class="text-xs text-indigo-600 hover:underline" @click.stop>{{ getTesterPhone(job.contractor) }}</a>
                                </div>
                                <div v-if="getJobStageForMap(job) !== 'idle' && getJobStageForMap(job) !== 'offline'" class="mt-1">
                                  <span :class="mapStagePillClass(getJobStageForMap(job))" class="text-[9px] px-1.5 py-0.5 rounded font-medium">{{ mapStageLabel(getJobStageForMap(job)) }}</span>
                                </div>
                              </div>
                            </div>
                            <span :class="testerResponseClass(job.testerResponse)" class="text-xs px-2 py-0.5 rounded-full">{{ job.testerResponse || 'pending' }}</span>
                          </div>
                        </div>
                        <div v-else class="text-xs text-gray-400 italic mb-3">
                          <i class="fa-solid fa-user-slash mr-1"></i>No tester assigned
                          <!-- Team leader quick-assign -->
                          <select v-if="isTeamLeader && teamMembers.length > 0" @change="assignTesterFromTeam(job, $event)" class="ml-2 text-xs border border-gray-200 rounded px-1 py-0.5 outline-none">
                            <option value="">Assign...</option>
                            <option v-for="m in teamMembers" :key="m.id" :value="m.id">{{ m.name }}</option>
                          </select>
                        </div>

                        <!-- Team leaders -->
                        <div v-if="job.teamLeaders && job.teamLeaders.length > 0" class="flex flex-wrap gap-1 mb-3">
                          <span v-for="tl in job.teamLeaders" :key="tl.id" class="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700 text-xs font-medium">
                            <i class="fa-solid fa-star mr-1 text-yellow-500" style="font-size:8px"></i>{{ tl.name }}
                          </span>
                        </div>

                        <!-- Notes -->
                        <div v-if="job.testerNotes" class="mb-2 text-xs bg-red-50 text-red-600 rounded-lg p-2.5"><span class="font-semibold uppercase text-[10px] tracking-wide">Tester Notes</span><p class="mt-0.5">{{ job.testerNotes }}</p></div>
                        <div v-if="job.notesForTester" class="mb-2 text-xs bg-yellow-50 text-yellow-700 rounded-lg p-2.5"><span class="font-semibold uppercase text-[10px] tracking-wide">Instructions for Tester</span><p class="mt-0.5">{{ job.notesForTester }}</p></div>
                        <div v-if="job.notes && !isTeamLeader" class="mb-2 text-xs text-gray-500 bg-gray-50 rounded-lg p-2.5"><span class="font-semibold text-gray-400 uppercase text-[10px] tracking-wide">General Notes</span><p class="mt-0.5">{{ job.notes }}</p></div>

                        <!-- Team leader inline notes editor -->
                        <div v-if="isTeamLeader" class="mb-2" @click.stop>
                          <label class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide block mb-1">Notes</label>
                          <textarea v-model="job.notes" rows="2" class="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-indigo-400" placeholder="Add notes..."></textarea>
                          <button @click="saveTeamLeaderNotes(job)" class="mt-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium">Save Notes</button>
                        </div>

                        <!-- SMS status + inline controls -->
                        <div class="flex items-center justify-between mt-2 pt-2 border-t border-gray-100" @click.stop>
                          <div class="flex items-center space-x-2">
                            <select v-model="job.status" @change="isTeamLeader ? saveTeamLeaderStatus(job) : updateJobStatus(job)" :class="statusSelectClass(job.status)" class="text-xs font-medium px-2 py-1 rounded-lg border outline-none cursor-pointer">
                              <option>ON-CALL</option><option>FIRM</option><option>COMMITTED</option><option>COMPLETED</option><option>CANCELLED</option>
                            </select>
                            <select v-if="job.contractor" v-model="job.testerResponse" @change="updateTesterResponse(job)" :class="testerResponseSelectClass(job.testerResponse)" class="text-xs font-medium px-2 py-1 rounded-lg border outline-none cursor-pointer">
                              <option value="pending">Pending</option><option value="accepted">Accepted</option><option value="rejected">Rejected</option>
                            </select>
                          </div>
                          <div v-if="job.clientSmsStatus" class="flex items-center text-xs text-gray-500">
                            <i v-if="job.clientSmsStatus === 'FIRM'" class="fa-solid fa-circle-check text-green-500 mr-1 cursor-pointer" @click="openChangeResolveModal(job)" title="FIRM via SMS – click to resolve"></i>
                            <i v-else-if="job.clientSmsStatus === 'CHANGED'" class="fa-solid fa-circle-question text-yellow-500 mr-1 cursor-pointer" @click="openChangeResolveModal(job)" title="Click to resolve"></i>
                            <i v-else-if="job.clientSmsStatus === 'CANCELLED'" class="fa-solid fa-circle-xmark text-red-500 mr-1 cursor-pointer" @click="openChangeResolveModal(job)" title="CANCELLED via SMS – click to resolve"></i>
                            {{ job.clientSmsStatus }} via SMS
                          </div>
                        </div>
                      </template>

                      <!-- EDIT MODE -->
                      <template v-else>
                        <div @click.stop>
                          <div class="flex items-center justify-between mb-3">
                            <h4 class="text-sm font-bold text-gray-900">Edit Job</h4>
                            <div class="flex items-center space-x-2">
                              <button @click="cancelEditJob(job)" class="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-50">Cancel</button>
                              <button @click="saveJob(job)" :disabled="jobSaving" class="text-xs text-white bg-indigo-600 hover:bg-indigo-700 px-3 py-1.5 rounded disabled:opacity-50">
                                <i v-if="jobSaving" class="fa-solid fa-spinner fa-spin mr-1"></i>Save
                              </button>
                            </div>
                          </div>
                          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Date &amp; Time</label>
                              <flat-pickr v-model="job.date" :config="dateTimeConfig" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none" />
                            </div>
                            <div>
                              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Status</label>
                              <select v-model="job.status" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none">
                                <option>ON-CALL</option><option>FIRM</option><option>COMMITTED</option><option>COMPLETED</option><option>CANCELLED</option>
                              </select>
                            </div>
                            <div>
                              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Job Contact</label>
                              <input type="text" v-model="job.jobContact" placeholder="Job-specific contact" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none" />
                            </div>
                            <div>
                              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Job Phone</label>
                              <input type="text" v-model="job.jobPhone" placeholder="Job-specific mobile" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none" />
                            </div>
                            <div class="sm:col-span-2">
                              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Job Email</label>
                              <input type="text" v-model="job.jobEmail" placeholder="Job-specific email" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none" />
                            </div>
                            <div>
                              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Tester</label>
                              <select v-model="job.contractor" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none">
                                <option :value="null">-- Select --</option>
                                <option v-for="c in contractors" :key="c.id" :value="c">{{ c.name }}</option>
                              </select>
                            </div>
                            <div class="sm:col-span-2">
                              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Team Leaders</label>
                              <div class="flex flex-wrap gap-1.5 mb-1.5">
                                <span v-for="tl in (job.teamLeaders || [])" :key="tl.id" class="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-xs font-medium">
                                  {{ tl.name }}
                                  <button @click="removeTeamLeaderFromJob(job, tl.id)" class="ml-1 text-yellow-600 hover:text-yellow-800">&times;</button>
                                </span>
                              </div>
                              <select v-if="availableTeamLeadersForJob(job).length > 0" @change="addTeamLeaderToJob(job, $event)" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none">
                                <option value="">Add team leader...</option>
                                <option v-for="tl in availableTeamLeadersForJob(job)" :key="tl.id" :value="tl.id">{{ tl.name }}</option>
                              </select>
                            </div>
                            <div v-if="customFieldsEnabled">
                              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Service</label>
                              <select :value="jobService(job)" @change="setJobService(job, $event.target.value)" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none">
                                <option v-for="s in customServices" :key="s.key" :value="s.key">{{ s.label }}</option>
                              </select>
                            </div>
                            <template v-if="!customFieldsEnabled">
                              <div>
                                <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Mix Code</label>
                                <input type="text" v-model="job.mixCode" placeholder="e.g. N32" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none" />
                              </div>
                              <div>
                                <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Meters (m&sup3;)</label>
                                <input type="text" v-model="job.meters" placeholder="e.g. 4" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none" />
                              </div>
                            </template>
                            <div>
                              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Duration (min)</label>
                              <input type="number" v-model="job.duration" placeholder="e.g. 60" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none" />
                            </div>
                            <div v-if="!customFieldsEnabled" class="sm:col-span-2">
                              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Pour Location</label>
                              <input type="text" v-model="job.pourLocation" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none" />
                            </div>
                            <custom-fields-input v-if="customFieldsEnabled" :job="job" :fields="jobCardFields(job)" />
                            <div class="sm:col-span-2">
                              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Requirements</label>
                              <input type="text" v-model="job.requirements" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none" />
                            </div>
                            <div class="sm:col-span-2">
                              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Notes for Tester</label>
                              <input type="text" v-model="job.notesForTester" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none" />
                            </div>
                            <div class="sm:col-span-2">
                              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Notes</label>
                              <textarea v-model="job.notes" rows="2" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 outline-none"></textarea>
                            </div>
                            <div class="flex items-center">
                              <label class="text-xs font-medium text-gray-600 mr-3">Flexible</label>
                              <div @click="job.flexible = !job.flexible" :class="job.flexible ? 'bg-green-500' : 'bg-gray-300'" class="relative w-10 h-5 rounded-full cursor-pointer transition-colors">
                                <div :class="job.flexible ? 'translate-x-5' : 'translate-x-0'" class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform"></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>

                <!-- COMPLETED / CANCELLED JOBS -->
                <div class="mb-4">
                  <div class="flex items-center justify-between mb-3">
                    <button @click="toggleCompletedJobs()" class="flex items-center text-sm font-bold text-gray-500 uppercase hover:text-gray-700 transition-colors">
                      <i :class="showCompleted ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-right'" class="mr-2 text-xs text-gray-400"></i>
                      Completed / Cancelled
                      <span class="ml-2 text-xs font-normal bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">{{ completedJobs.length }}</span>
                    </button>
                  </div>
                  <template v-if="showCompleted">
                    <div v-if="completedJobsLoading" class="flex items-center justify-center py-8">
                      <i class="fa-solid fa-spinner fa-spin text-gray-400 mr-2"></i>
                      <span class="text-sm text-gray-400">Loading completed jobs...</span>
                    </div>
                    <div v-else>
                      <div v-if="completedJobs.length === 0" class="text-center text-sm text-gray-400 py-6">No completed or cancelled jobs</div>
                      <div :class="jobViewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3' : 'space-y-3'">
                        <div
                          v-for="job in completedJobs"
                          :key="job.id"
                          @click="!job._editing && selectJob(job)"
                          :class="[
                            'rounded-xl border p-4 transition',
                            job._editing ? '' : 'cursor-pointer',
                            job.status === 'COMPLETED' ? 'bg-green-100 border-green-300' : job.status === 'CANCELLED' ? 'bg-red-100 border-red-300' : 'bg-white border-gray-200',
                            selectedJobId === job.id && !job._editing ? 'ring-1 ring-indigo-200' : ''
                          ]"
                        >
                          <!-- VIEW MODE -->
                          <template v-if="!job._editing">
                            <div class="flex items-start justify-between mb-3">
                              <div class="flex items-baseline flex-wrap gap-x-3 gap-y-1">
                                <span v-if="job.jobNumber" class="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">#{{ job.jobNumber }}</span>
                                <span class="text-xl font-bold text-gray-900 leading-none">{{ formatJobTime(job) }}</span>
                                <span class="text-sm text-gray-400">{{ formatJobDateShort(job) }}</span>
                                <span :class="statusBadgeClass(job.status)" class="text-xs font-medium px-2 py-0.5 rounded-full">{{ job.status }}</span>
                              </div>
                              <div class="flex items-center space-x-1 flex-shrink-0" @click.stop>
                                <button v-if="!isTeamLeader" @click="openSmsModal(job)" class="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50" title="Send SMS"><i class="fa-solid fa-comment-sms text-sm"></i></button>
                                <button v-if="!isTeamLeader" @click="startEditJob(job)" class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100" title="Edit"><i class="fa-solid fa-pen text-sm"></i></button>
                                <button v-if="!isTeamLeader" @click="duplicateJob(job)" class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100" title="Duplicate"><i class="fa-solid fa-copy text-sm"></i></button>
                                <button v-if="!isTeamLeader" @click="confirmDeleteJob(job)" class="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" title="Delete"><i class="fa-solid fa-trash text-sm"></i></button>
                              </div>
                            </div>
                            <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 mb-3">
                              <custom-fields-display v-if="customFieldsEnabled" :job="job" :fields="jobCardFields(job)" />
                              <template v-else>
                                <span v-if="job.mixCode" class="flex items-center"><i class="fa-solid fa-flask text-gray-400 mr-1.5 text-xs"></i><span class="font-medium">{{ job.mixCode }}</span></span>
                                <span v-if="job.meters" class="flex items-center"><i class="fa-solid fa-ruler text-gray-400 mr-1.5 text-xs"></i>{{ job.meters }}m&sup3;</span>
                              </template>
                              <span v-if="job.duration" class="flex items-center"><i class="fa-regular fa-clock text-gray-400 mr-1.5 text-xs"></i>{{ job.duration }}min</span>
                              <span v-if="!customFieldsEnabled && job.pourLocation" class="flex items-center text-gray-500"><i class="fa-solid fa-location-crosshairs text-gray-400 mr-1.5 text-xs"></i>{{ job.pourLocation }}</span>
                            </div>
                            <div v-if="job.requirements" class="mb-3 text-xs text-gray-500 bg-blue-50 rounded-lg p-2.5">
                              <span class="font-semibold text-blue-700 uppercase text-[10px] tracking-wide">Requirements</span>
                              <p class="mt-0.5 text-blue-800">{{ job.requirements }}</p>
                            </div>
                            <div v-if="job.contractor && job.contractor.name" class="bg-white bg-opacity-60 rounded-lg p-3 mb-3">
                              <div class="flex items-center justify-between">
                                <div class="flex items-center">
                                  <div class="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold bg-gray-200 text-gray-500">
                                    {{ initials(job.contractor.name) }}
                                  </div>
                                  <div class="ml-2.5">
                                    <span class="text-sm font-medium text-gray-800">{{ job.contractor.name }}</span>
                                    <div v-if="getTesterPhone(job.contractor)" class="mt-0.5">
                                      <a :href="'tel:' + getTesterPhone(job.contractor)" class="text-xs text-indigo-600 hover:underline" @click.stop>{{ getTesterPhone(job.contractor) }}</a>
                                    </div>
                                  </div>
                                </div>
                                <span :class="testerResponseClass(job.testerResponse)" class="text-xs px-2 py-0.5 rounded-full">{{ job.testerResponse || 'pending' }}</span>
                              </div>
                            </div>
                            <div v-else class="text-xs text-gray-400 italic mb-3">
                              <i class="fa-solid fa-user-slash mr-1"></i>No tester assigned
                            </div>

                            <!-- Team leaders (completed jobs) -->
                            <div v-if="job.teamLeaders && job.teamLeaders.length > 0" class="flex flex-wrap gap-1 mb-3">
                              <span v-for="tl in job.teamLeaders" :key="tl.id" class="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700 text-xs font-medium">
                                <i class="fa-solid fa-star mr-1 text-yellow-500" style="font-size:8px"></i>{{ tl.name }}
                              </span>
                            </div>

                            <div v-if="job.testerNotes" class="mb-2 text-xs bg-red-50 text-red-600 rounded-lg p-2.5"><span class="font-semibold uppercase text-[10px] tracking-wide">Tester Notes</span><p class="mt-0.5">{{ job.testerNotes }}</p></div>
                            <div v-if="job.notesForTester" class="mb-2 text-xs bg-yellow-50 text-yellow-700 rounded-lg p-2.5"><span class="font-semibold uppercase text-[10px] tracking-wide">Instructions for Tester</span><p class="mt-0.5">{{ job.notesForTester }}</p></div>
                            <div v-if="job.notes" class="mb-2 text-xs text-gray-500 bg-gray-50 rounded-lg p-2.5"><span class="font-semibold text-gray-400 uppercase text-[10px] tracking-wide">General Notes</span><p class="mt-0.5">{{ job.notes }}</p></div>
                            <div class="flex items-center justify-between mt-2 pt-2 border-t border-gray-200 border-opacity-50" @click.stop>
                              <div class="flex items-center space-x-2">
                                <select v-model="job.status" @change="updateJobStatus(job)" :class="statusSelectClass(job.status)" class="text-xs font-medium px-2 py-1 rounded-lg border outline-none cursor-pointer">
                                  <option>ON-CALL</option><option>FIRM</option><option>COMMITTED</option><option>COMPLETED</option><option>CANCELLED</option>
                                </select>
                              </div>
                            </div>
                          </template>
                          <!-- EDIT MODE -->
                          <template v-else>
                            <div @click.stop>
                              <div class="flex items-center justify-between mb-3">
                                <h4 class="text-sm font-bold text-gray-900">Edit Job</h4>
                                <div class="flex items-center space-x-2">
                                  <button @click="cancelEditJob(job)" class="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-50">Cancel</button>
                                  <button @click="saveJob(job)" :disabled="jobSaving" class="text-xs text-white bg-indigo-600 hover:bg-indigo-700 px-3 py-1.5 rounded disabled:opacity-50">
                                    <i v-if="jobSaving" class="fa-solid fa-spinner fa-spin mr-1"></i>Save
                                  </button>
                                </div>
                              </div>
                              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                  <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Date &amp; Time</label>
                                  <flat-pickr v-model="job.date" :config="dateTimeConfig" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none" />
                                </div>
                                <div>
                                  <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Status</label>
                                  <select v-model="job.status" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none">
                                    <option>ON-CALL</option><option>FIRM</option><option>COMMITTED</option><option>COMPLETED</option><option>CANCELLED</option>
                                  </select>
                                </div>
                                <div>
                                  <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Job Contact</label>
                                  <input type="text" v-model="job.jobContact" placeholder="Job-specific contact" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none" />
                                </div>
                                <div>
                                  <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Job Phone</label>
                                  <input type="text" v-model="job.jobPhone" placeholder="Job-specific mobile" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none" />
                                </div>
                                <div class="sm:col-span-2">
                                  <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Job Email</label>
                                  <input type="text" v-model="job.jobEmail" placeholder="Job-specific email" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none" />
                                </div>
                                <div>
                                  <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Tester</label>
                                  <select v-model="job.contractor" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none">
                                    <option :value="null">-- Select --</option>
                                    <option v-for="c in contractors" :key="c.id" :value="c">{{ c.name }}</option>
                                  </select>
                                </div>
                                <div class="sm:col-span-2">
                                  <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Team Leaders</label>
                                  <div class="flex flex-wrap gap-1.5 mb-1.5">
                                    <span v-for="tl in (job.teamLeaders || [])" :key="tl.id" class="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-xs font-medium">
                                      {{ tl.name }}
                                      <button @click="removeTeamLeaderFromJob(job, tl.id)" class="ml-1 text-yellow-600 hover:text-yellow-800">&times;</button>
                                    </span>
                                  </div>
                                  <select v-if="availableTeamLeadersForJob(job).length > 0" @change="addTeamLeaderToJob(job, $event)" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none">
                                    <option value="">Add team leader...</option>
                                    <option v-for="tl in availableTeamLeadersForJob(job)" :key="tl.id" :value="tl.id">{{ tl.name }}</option>
                                  </select>
                                </div>
                                <div v-if="customFieldsEnabled">
                                  <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Service</label>
                                  <select :value="jobService(job)" @change="setJobService(job, $event.target.value)" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none">
                                    <option v-for="s in customServices" :key="s.key" :value="s.key">{{ s.label }}</option>
                                  </select>
                                </div>
                                <template v-if="!customFieldsEnabled">
                                  <div>
                                    <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Mix Code</label>
                                    <input type="text" v-model="job.mixCode" placeholder="e.g. N32" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none" />
                                  </div>
                                  <div>
                                    <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Meters (m&sup3;)</label>
                                    <input type="text" v-model="job.meters" placeholder="e.g. 4" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none" />
                                  </div>
                                </template>
                                <div>
                                  <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Duration (min)</label>
                                  <input type="number" v-model="job.duration" placeholder="e.g. 60" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none" />
                                </div>
                                <div v-if="!customFieldsEnabled" class="sm:col-span-2">
                                  <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Pour Location</label>
                                  <input type="text" v-model="job.pourLocation" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none" />
                                </div>
                                <custom-fields-input v-if="customFieldsEnabled" :job="job" :fields="jobCardFields(job)" />
                                <div class="sm:col-span-2">
                                  <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Requirements</label>
                                  <input type="text" v-model="job.requirements" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none" />
                                </div>
                                <div class="sm:col-span-2">
                                  <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Notes for Tester</label>
                                  <input type="text" v-model="job.notesForTester" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 h-10 outline-none" />
                                </div>
                                <div class="sm:col-span-2">
                                  <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Notes</label>
                                  <textarea v-model="job.notes" rows="2" class="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 outline-none"></textarea>
                                </div>
                                <div class="flex items-center">
                                  <label class="text-xs font-medium text-gray-600 mr-3">Flexible</label>
                                  <div @click="job.flexible = !job.flexible" :class="job.flexible ? 'bg-green-500' : 'bg-gray-300'" class="relative w-10 h-5 rounded-full cursor-pointer transition-colors">
                                    <div :class="job.flexible ? 'translate-x-5' : 'translate-x-0'" class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform"></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </template>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>

              </div>
            </div>

            <!-- MAP TAB — Mini Tracker -->
            <div v-show="contextTab === 'map'" class="flex-1 relative overflow-hidden">
              <div id="enterprise-map" class="w-full h-full"></div>

              <!-- Overlay: Job cards panel (top-left) -->
              <div v-if="selectedProject && filteredCurrentJobs.length > 0" class="absolute top-3 left-3 z-10 w-80 max-h-[calc(100%-24px)] overflow-y-auto space-y-2">
                <div
                  v-for="job in filteredCurrentJobs"
                  :key="job.id"
                  @click="selectJob(job); centerMapOnJob(job)"
                  :class="[
                    'rounded-lg border shadow-lg p-3 cursor-pointer transition-all',
                    selectedJobId === job.id
                      ? 'bg-gray-900 border-indigo-500 ring-1 ring-indigo-400'
                      : 'bg-gray-900 bg-opacity-95 border-gray-700 hover:border-gray-500'
                  ]"
                >
                  <!-- Start time big & bold -->
                  <div class="flex items-center justify-between mb-2">
                    <div>
                      <div class="text-lg font-bold text-white leading-tight">{{ formatJobTime(job) }}</div>
                      <div class="text-xs text-gray-400">{{ formatJobDateShort(job) }}</div>
                    </div>
                    <span :class="mapStatusBadgeClass(job.status)" class="text-xs font-bold px-2.5 py-1 rounded-full">{{ job.status }}</span>
                  </div>

                  <!-- Tester info -->
                  <div v-if="job.contractor && job.contractor.name" class="flex items-center mb-2">
                    <div :class="['w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0', testerLocations[job.contractor.id] ? 'bg-green-900 text-green-300' : 'bg-gray-700 text-gray-400']">
                      {{ initials(job.contractor.name) }}
                    </div>
                    <div class="ml-2.5 flex-grow min-w-0">
                      <div class="text-sm font-medium text-white truncate">{{ job.contractor.name }}</div>
                      <div class="flex items-center text-xs">
                        <span v-if="testerLocations[job.contractor.id]" class="text-green-400 flex items-center">
                          <span class="w-1.5 h-1.5 rounded-full bg-green-400 mr-1 animate-pulse"></span>Online
                        </span>
                        <span v-else class="text-gray-500">Offline</span>
                        <span v-if="getJobStageForMap(job) !== 'idle' && getJobStageForMap(job) !== 'offline'" class="ml-2 text-gray-500">&middot;</span>
                        <span v-if="getJobStageForMap(job) !== 'idle' && getJobStageForMap(job) !== 'offline'" :class="mapStagePillClass(getJobStageForMap(job))" class="ml-1 px-1.5 py-0.5 rounded text-xs font-medium">
                          {{ mapStageLabel(getJobStageForMap(job)) }}
                        </span>
                      </div>
                    </div>
                    <span :class="testerResponseClass(job.testerResponse)" class="text-xs px-2 py-0.5 rounded-full flex-shrink-0">{{ job.testerResponse || 'pending' }}</span>
                  </div>
                  <div v-else class="text-xs text-gray-500 mb-2 italic">No tester assigned</div>

                  <!-- Milestone progress bar -->
                  <div v-if="job.contractor && getJobStageForMap(job) !== 'idle' && getJobStageForMap(job) !== 'offline'" class="flex space-x-1 mb-2">
                    <div v-for="(ms, i) in milestones(getJobStageForMap(job))" :key="i" :class="['h-1.5 flex-grow rounded-full', ms.active ? ms.color : 'bg-gray-700']" :title="ms.label"></div>
                  </div>

                  <!-- Job details row -->
                  <div class="flex items-center text-xs text-gray-400 space-x-3">
                    <span v-if="job.mixCode"><i class="fa-solid fa-flask mr-1 text-gray-600"></i>{{ job.mixCode }}</span>
                    <span v-if="job.meters"><i class="fa-solid fa-ruler mr-1 text-gray-600"></i>{{ job.meters }}m³</span>
                    <span v-if="job.pourLocation" class="truncate"><i class="fa-solid fa-location-dot mr-1 text-gray-600"></i>{{ job.pourLocation }}</span>
                  </div>
                </div>
              </div>

              <!-- Overlay: Stats bar (bottom) -->
              <div v-if="selectedProject" class="absolute bottom-3 left-3 right-3 z-10">
                <div class="bg-gray-900 bg-opacity-95 rounded-lg border border-gray-700 shadow-lg px-4 py-2.5 flex items-center justify-between text-xs">
                  <div class="flex items-center space-x-4">
                    <span class="text-gray-400"><span class="text-white font-bold">{{ mapStats.total }}</span> Jobs</span>
                    <span v-if="mapStats.enRoute" class="text-blue-400"><i class="fa-solid fa-route mr-1"></i>{{ mapStats.enRoute }} En Route</span>
                    <span v-if="mapStats.arrived" class="text-yellow-400"><i class="fa-solid fa-flag-checkered mr-1"></i>{{ mapStats.arrived }} Arrived</span>
                    <span v-if="mapStats.testing" class="text-purple-400"><i class="fa-solid fa-vial mr-1"></i>{{ mapStats.testing }} Testing</span>
                    <span v-if="mapStats.completed" class="text-green-400"><i class="fa-solid fa-circle-check mr-1"></i>{{ mapStats.completed }} Done</span>
                  </div>
                  <div class="flex items-center space-x-3 text-gray-500">
                    <span><i class="fa-solid fa-user-helmet-safety mr-1"></i>{{ mapStats.testersOnline }} online</span>
                    <span>{{ selectedProject.formattedAddress || '' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- SMS TAB -->
            <div v-show="contextTab === 'sms'" class="flex-1 flex flex-col overflow-hidden">
              <div v-if="!selectedJob" class="flex-1 flex items-center justify-center text-gray-400 text-sm p-4 text-center">Select a job from the Jobs tab to view its SMS history</div>
              <template v-else>
                <div class="px-4 py-2 bg-gray-50 border-b border-gray-200 flex-shrink-0 text-xs text-gray-500">
                  SMS for: <span class="font-medium text-gray-700">{{ selectedJob.company || selectedProject.company }}</span> &mdash; {{ formatJobDate(selectedJob) }}
                </div>
                <div class="flex-1 overflow-y-auto flex flex-col-reverse" style="background: #f2f2f7;" ref="smsMessages">
                  <div v-if="smsLoading" class="flex justify-center py-4"><i class="fa-solid fa-spinner fa-spin text-gray-400"></i></div>
                  <div v-else-if="smsHistory.length === 0" class="text-center text-gray-400 text-sm py-4">
                    <i class="fa-solid fa-inbox text-2xl text-gray-300 mb-2 block"></i>No messages yet
                  </div>
                  <div v-else class="px-3 py-3 space-y-1">
                    <div v-for="(msg, idx) in smsHistory" :key="idx" class="flex" :class="msg.direction === 'inbound' ? 'justify-start' : 'justify-end'">
                      <div class="max-w-xs">
                        <div class="imsg-bubble px-3 py-2 text-sm" :class="msg.direction === 'inbound' ? 'imsg-recv' : 'imsg-sent'">
                          <div>{{ msg.body }}</div>
                          <span v-if="msg.classification" class="inline-block mt-1 text-xs px-1.5 py-0.5 rounded-full font-medium"
                            :class="{
                              'bg-green-400 bg-opacity-30 text-green-100': msg.classification === 'FIRM' && msg.direction !== 'inbound',
                              'bg-green-100 text-green-700': msg.classification === 'FIRM' && msg.direction === 'inbound',
                              'bg-yellow-400 bg-opacity-30 text-yellow-100': msg.classification === 'CHANGED' && msg.direction !== 'inbound',
                              'bg-yellow-100 text-yellow-700': msg.classification === 'CHANGED' && msg.direction === 'inbound',
                              'bg-red-400 bg-opacity-30 text-red-100': msg.classification === 'CANCELLED' && msg.direction !== 'inbound',
                              'bg-red-100 text-red-700': msg.classification === 'CANCELLED' && msg.direction === 'inbound',
                            }">{{ msg.classification }}</span>
                        </div>
                        <div class="px-1 mt-0.5" :class="msg.direction === 'inbound' ? 'text-left' : 'text-right'">
                          <span class="text-xs text-gray-400">{{ formatSmsTimestamp(msg.timestamp) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="border-t border-gray-200 p-3 flex-shrink-0 bg-white">
                  <div class="flex items-end space-x-2">
                    <div class="flex-1 rounded-full bg-gray-100 border border-gray-200 px-4 py-2">
                      <textarea v-model="smsReplyText" placeholder="iMessage" rows="1" class="w-full bg-transparent text-sm text-gray-700 outline-none resize-none leading-5" style="max-height: 80px;" @input="$event.target.style.height = 'auto'; $event.target.style.height = $event.target.scrollHeight + 'px';"></textarea>
                    </div>
                    <button @click="sendContextSms()" :disabled="!smsReplyText.trim() || smsLoading" class="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center flex-shrink-0 mb-0.5 disabled:opacity-30">
                      <i class="fa-solid fa-arrow-up text-xs font-bold"></i>
                    </button>
                  </div>
                </div>
              </template>
            </div>
          </template>
        </div>
      </div>

    </div>

    <!-- ==================== DELETE MODAL ==================== -->
    <div v-if="deleteConfirm" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-gray-900 bg-opacity-50" @click="deleteConfirm = false"></div>
      <div class="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4 relative z-10">
        <div class="flex items-center justify-center mb-4">
          <div class="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center"><i class="fa-solid fa-triangle-exclamation text-red-500 text-2xl"></i></div>
        </div>
        <h3 class="text-lg font-bold text-gray-900 text-center mb-2">Delete Job</h3>
        <p class="text-sm text-gray-500 text-center mb-6">Are you sure? This cannot be undone.</p>
        <button @click="deleteJob()" class="w-full rounded-lg bg-red-600 text-white font-medium py-3 text-sm hover:bg-red-700 mb-3">Delete Job</button>
        <button @click="deleteConfirm = false" class="w-full rounded-lg border border-gray-300 text-gray-700 font-medium py-3 text-sm hover:bg-gray-50">Cancel</button>
      </div>
    </div>

    <!-- ==================== CLOSE PROJECT CONFIRMATION ==================== -->
    <div v-if="closeProjectTarget" class="fixed inset-0 z-50 flex items-center justify-center" style="backdrop-filter:blur(2px)">
      <div class="absolute inset-0 bg-gray-900 bg-opacity-50" @click="closeProjectTarget = null"></div>
      <div class="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 relative z-10">
        <div class="px-6 py-5 border-b border-gray-200">
          <h3 class="text-lg font-bold text-gray-900">
            <i class="fa-solid fa-box-archive text-amber-600 mr-2"></i>Close Project
          </h3>
        </div>
        <div class="px-6 py-5">
          <p class="text-sm text-gray-600 mb-4">Are you sure you want to close this project?</p>
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
            <div class="flex items-center justify-between">
              <span class="text-xs text-gray-500">Company</span>
              <span class="text-sm font-medium text-gray-900">{{ closeProjectTarget.company }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-xs text-gray-500">Project</span>
              <span class="text-sm font-medium text-gray-900">{{ closeProjectTarget.projectName }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-xs text-gray-500">Jobs</span>
              <span class="text-sm font-medium text-gray-900">{{ getJobCount(closeProjectTarget) }}</span>
            </div>
          </div>
          <p class="text-xs text-gray-400 mt-3">Closed projects can be reopened later from the Archived tab.</p>
        </div>
        <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl flex justify-end gap-2">
          <button @click="closeProjectTarget = null" class="px-4 h-10 rounded-lg text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300">Cancel</button>
          <button @click="confirmCloseProject()" :disabled="closingProject" class="px-5 h-10 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
            <i v-if="closingProject" class="fa-solid fa-spinner fa-spin mr-1.5"></i>
            {{ closingProject ? 'Closing...' : 'Close Project' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ==================== REOPEN PROJECT CONFIRMATION ==================== -->
    <div v-if="reopenProjectTarget" class="fixed inset-0 z-50 flex items-center justify-center" style="backdrop-filter:blur(2px)">
      <div class="absolute inset-0 bg-gray-900 bg-opacity-50" @click="reopenProjectTarget = null"></div>
      <div class="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 relative z-10">
        <div class="px-6 py-5 border-b border-gray-200">
          <h3 class="text-lg font-bold text-gray-900">
            <i class="fa-solid fa-lock-open text-green-600 mr-2"></i>Reopen Project
          </h3>
        </div>
        <div class="px-6 py-5">
          <p class="text-sm text-gray-600 mb-4">Are you sure you want to reopen this project?</p>
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
            <div class="flex items-center justify-between">
              <span class="text-xs text-gray-500">Company</span>
              <span class="text-sm font-medium text-gray-900">{{ reopenProjectTarget.company }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-xs text-gray-500">Project</span>
              <span class="text-sm font-medium text-gray-900">{{ reopenProjectTarget.projectName }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-xs text-gray-500">Jobs</span>
              <span class="text-sm font-medium text-gray-900">{{ getJobCount(reopenProjectTarget) }}</span>
            </div>
          </div>
          <p class="text-xs text-gray-400 mt-3">The project will be moved back to the Current tab.</p>
        </div>
        <div class="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button @click="reopenProjectTarget = null" class="px-4 h-10 rounded-lg text-sm font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-100">Cancel</button>
          <button @click="confirmReopenProject()" :disabled="reopeningProject" class="px-5 h-10 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
            <i v-if="reopeningProject" class="fa-solid fa-spinner fa-spin mr-1.5"></i>
            {{ reopeningProject ? 'Reopening...' : 'Reopen Project' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ==================== AUTO ARCHIVE MODAL ==================== -->
    <div v-if="autoArchiveModal" class="fixed inset-0 z-50 flex items-center justify-center" style="backdrop-filter:blur(2px)">
      <div class="absolute inset-0 bg-gray-900 bg-opacity-50" @click="!autoArchiveRunning && (autoArchiveModal = false)"></div>
      <div class="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 relative z-10 flex flex-col" style="max-height:85vh">
        <div class="px-6 py-5 border-b border-gray-200">
          <h3 class="text-lg font-bold text-gray-900 mb-4">
            <i class="fa-solid fa-box-archive text-amber-600 mr-2"></i>Project Management
          </h3>
          <!-- Tabs -->
          <div class="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button
              v-for="t in [{key:'archive',label:'Archive',icon:'fa-box-archive'},{key:'reopen',label:'Reopen',icon:'fa-box-open'},{key:'empty',label:'Close Empty',icon:'fa-trash-can'}]"
              :key="t.key"
              @click="!autoArchiveRunning && (autoArchiveTab = t.key, autoArchiveDone = false, autoArchiveLog = [])"
              :class="autoArchiveTab === t.key ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'"
              class="flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all"
            >
              <i :class="'fa-solid ' + t.icon" class="mr-1.5"></i>{{ t.label }}
            </button>
          </div>
        </div>
        <div class="px-6 py-5 overflow-y-auto flex-1">
          <!-- ARCHIVE TAB -->
          <template v-if="autoArchiveTab === 'archive'">
            <template v-if="!autoArchiveDone">
              <p class="text-sm text-gray-600 mb-4">Archive all open projects where the newest job is older than a specified number of days.</p>
              <div class="mb-4">
                <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Days since last job</label>
                <input type="number" v-model.number="autoArchiveDays" min="1" placeholder="e.g. 30" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400" />
              </div>
            </template>
            <template v-else>
              <div class="text-center py-2">
                <i class="fa-solid fa-circle-check text-green-500 text-3xl mb-3"></i>
                <p class="text-sm font-medium text-gray-900 mb-1">Archive Complete</p>
                <p class="text-sm text-gray-500">{{ autoArchiveCount }} project{{ autoArchiveCount === 1 ? '' : 's' }} archived.</p>
              </div>
            </template>
          </template>

          <!-- REOPEN TAB -->
          <template v-if="autoArchiveTab === 'reopen'">
            <template v-if="!autoArchiveDone">
              <p class="text-sm text-gray-600 mb-4">Reopen archived projects that have a job within the specified number of days.</p>
              <div class="mb-4">
                <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Days to look back</label>
                <input type="number" v-model.number="autoArchiveDays" min="1" placeholder="e.g. 30" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400" />
              </div>
            </template>
            <template v-else>
              <div class="text-center py-2">
                <i class="fa-solid fa-circle-check text-green-500 text-3xl mb-3"></i>
                <p class="text-sm font-medium text-gray-900 mb-1">Reopen Complete</p>
                <p class="text-sm text-gray-500">{{ autoArchiveCount }} project{{ autoArchiveCount === 1 ? '' : 's' }} reopened.</p>
              </div>
            </template>
          </template>

          <!-- CLOSE EMPTY TAB -->
          <template v-if="autoArchiveTab === 'empty'">
            <template v-if="!autoArchiveDone">
              <p class="text-sm text-gray-600 mb-4">Close all open projects that have zero active jobs (ON-CALL, FIRM, or COMMITTED).</p>
            </template>
            <template v-else>
              <div class="text-center py-2">
                <i class="fa-solid fa-circle-check text-green-500 text-3xl mb-3"></i>
                <p class="text-sm font-medium text-gray-900 mb-1">Close Empty Complete</p>
                <p class="text-sm text-gray-500">{{ autoArchiveCount }} empty project{{ autoArchiveCount === 1 ? '' : 's' }} closed.</p>
              </div>
            </template>
          </template>

          <!-- Progress bar -->
          <div v-if="autoArchiveRunning" class="mt-4">
            <div class="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>Checking projects...</span>
              <span>{{ autoArchiveProgress }} / {{ autoArchiveTotal }}</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2 mb-3">
              <div class="bg-amber-500 h-2 rounded-full transition-all" :style="{ width: autoArchiveTotal ? (autoArchiveProgress / autoArchiveTotal * 100) + '%' : '0%' }"></div>
            </div>
          </div>

          <!-- Live log -->
          <div v-if="autoArchiveLog.length" class="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto" ref="autoArchiveLogRef">
            <div v-for="(entry, i) in autoArchiveLog" :key="i" class="text-xs font-mono leading-relaxed" :class="entry.type === 'match' ? 'text-amber-700' : entry.type === 'done' ? 'text-green-700 font-semibold' : 'text-gray-400'">
              {{ entry.msg }}
            </div>
          </div>
        </div>
        <div class="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button @click="autoArchiveModal = false; autoArchiveDone = false; autoArchiveLog = []" :disabled="autoArchiveRunning" class="px-4 h-10 rounded-lg text-sm font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-100 disabled:opacity-50">
            {{ autoArchiveDone ? 'Close' : 'Cancel' }}
          </button>
          <button v-if="!autoArchiveDone && autoArchiveTab === 'archive'" @click="runAutoArchive()" :disabled="autoArchiveRunning || !autoArchiveDays || autoArchiveDays < 1" class="px-5 h-10 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
            <i v-if="autoArchiveRunning" class="fa-solid fa-spinner fa-spin mr-1.5"></i>
            {{ autoArchiveRunning ? 'Archiving...' : 'Archive Projects' }}
          </button>
          <button v-if="!autoArchiveDone && autoArchiveTab === 'reopen'" @click="runAutoReopen()" :disabled="autoArchiveRunning || !autoArchiveDays || autoArchiveDays < 1" class="px-5 h-10 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
            <i v-if="autoArchiveRunning" class="fa-solid fa-spinner fa-spin mr-1.5"></i>
            {{ autoArchiveRunning ? 'Reopening...' : 'Reopen Projects' }}
          </button>
          <button v-if="!autoArchiveDone && autoArchiveTab === 'empty'" @click="runCloseEmpty()" :disabled="autoArchiveRunning" class="px-5 h-10 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
            <i v-if="autoArchiveRunning" class="fa-solid fa-spinner fa-spin mr-1.5"></i>
            {{ autoArchiveRunning ? 'Closing...' : 'Close Empty Projects' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ==================== SMS SEND MODAL ==================== -->
    <div v-if="smsModal" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-gray-900 bg-opacity-50" @click="smsModal = false"></div>
      <div class="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 relative z-10 flex flex-col" style="max-height: 85vh;">
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <h3 class="text-lg font-bold text-gray-900">Send SMS</h3>
          <button @click="smsModal = false" class="text-gray-400 hover:text-gray-600"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <!-- Job details card -->
        <div v-if="smsModalJob" class="px-4 pt-3 pb-1 flex-shrink-0">
          <div class="bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-3">
            <div class="flex items-center gap-3">
              <div class="flex items-center gap-1.5 min-w-0">
                <label class="text-xs text-indigo-400 flex-shrink-0 font-medium">Status</label>
                <select v-model="smsModalJob.status" @change="smsModalJobDirty = true"
                  class="text-xs font-medium rounded-md px-2 py-1.5 outline-none border cursor-pointer bg-white"
                  :class="{
                    'text-yellow-700 border-yellow-200': smsModalJob.status === 'ON-CALL',
                    'text-green-700 border-green-200': smsModalJob.status === 'FIRM',
                    'text-blue-700 border-blue-200': smsModalJob.status === 'COMMITTED',
                    'text-red-700 border-red-200': smsModalJob.status === 'CANCELLED',
                    'text-gray-700 border-gray-200': smsModalJob.status === 'COMPLETED',
                  }">
                  <option>ON-CALL</option>
                  <option>FIRM</option>
                  <option>COMMITTED</option>
                  <option>CANCELLED</option>
                  <option>COMPLETED</option>
                </select>
              </div>
              <div class="flex items-center gap-1.5 flex-1 min-w-0">
                <label class="text-xs text-indigo-400 flex-shrink-0 font-medium">Date &amp; Time</label>
                <flat-pickr v-model="smsModalJob.date" :config="dateTimeConfig" @on-change="smsModalJobDirty = true"
                  class="text-xs border border-indigo-200 rounded-md px-2 py-1.5 outline-none w-full bg-white" />
              </div>
              <div class="flex items-center gap-1.5 min-w-0" style="max-width: 120px;">
                <label class="text-xs text-indigo-400 flex-shrink-0 font-medium">Meters</label>
                <input type="number" v-model="smsModalJob.meters" @input="smsModalJobDirty = true"
                  class="text-xs border border-indigo-200 rounded-md px-2 py-1.5 outline-none w-full bg-white" placeholder="0" />
              </div>
              <button v-if="smsModalJobDirty && !smsModalJobSaved" @click="saveSmsModalJob()" :disabled="smsModalJobSaving"
                class="flex-shrink-0 text-xs font-medium bg-indigo-600 text-white px-3 py-1.5 rounded-md hover:bg-indigo-700 disabled:opacity-50">
                <i v-if="smsModalJobSaving" class="fa-solid fa-spinner fa-spin mr-1"></i>
                Save Job
              </button>
              <span v-if="smsModalJobSaved" class="flex-shrink-0 text-xs text-green-600 font-semibold bg-green-50 border border-green-200 rounded-md px-3 py-1.5">
                <i class="fa-solid fa-check mr-1"></i>Job Saved
              </span>
            </div>
          </div>
        </div>
        <!-- To field -->
        <div class="px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <div class="flex items-center">
            <label class="text-xs text-gray-400 mr-2">To:</label>
            <input type="text" v-model="smsModalPhone" class="flex-1 text-sm text-gray-700 bg-transparent outline-none" />
          </div>
        </div>
        <!-- Template selector -->
        <div v-if="visibleSmsTemplates.length > 0" class="px-4 py-2 border-b border-gray-100 flex-shrink-0">
          <div class="flex items-center">
            <label class="text-xs text-gray-400 mr-2">Template:</label>
            <select v-model="selectedSmsTemplateId" @change="applySmsTemplate()"
              class="flex-1 text-sm text-gray-700 bg-transparent outline-none border-none cursor-pointer">
              <option value="">Custom message</option>
              <option v-for="t in visibleSmsTemplates" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
          </div>
        </div>
        <!-- Shortcodes -->
        <div class="px-4 py-2 border-b border-gray-100 flex-shrink-0">
          <p class="text-xs text-gray-400 mb-1.5 text-left">Shortcodes — click to insert:</p>
          <div class="flex flex-wrap gap-1.5">
            <button v-for="sc in smsShortcodes" :key="sc.code" @click="insertSmsShortcode(sc.code)"
              class="inline-flex items-center px-2.5 py-1 rounded-md bg-indigo-50 text-indigo-700 text-xs font-medium hover:bg-indigo-100 transition-colors cursor-pointer">
              <code class="mr-1">{{ sc.code }}</code>
              <span class="text-indigo-400 font-normal">{{ sc.label }}</span>
            </button>
          </div>
        </div>
        <!-- Message history (iMessage style) -->
        <div class="flex-1 overflow-y-auto flex flex-col-reverse" style="background: #f2f2f7; min-height: 120px;">
          <div v-if="smsModalHistory.length > 0" class="px-3 py-3 space-y-1">
            <div v-for="(msg, idx) in smsModalHistory" :key="idx" class="flex" :class="msg.direction === 'inbound' ? 'justify-start' : 'justify-end'">
              <div class="max-w-xs">
                <div class="imsg-bubble px-3 py-2 text-sm" :class="msg.direction === 'inbound' ? 'imsg-recv' : 'imsg-sent'">
                  <div>{{ msg.body }}</div>
                  <span v-if="msg.classification" class="inline-block mt-1 text-xs px-1.5 py-0.5 rounded-full font-medium"
                    :class="{
                      'bg-green-400 bg-opacity-30 text-green-100': msg.classification === 'FIRM' && msg.direction !== 'inbound',
                      'bg-green-100 text-green-700': msg.classification === 'FIRM' && msg.direction === 'inbound',
                      'bg-yellow-400 bg-opacity-30 text-yellow-100': msg.classification === 'CHANGED' && msg.direction !== 'inbound',
                      'bg-yellow-100 text-yellow-700': msg.classification === 'CHANGED' && msg.direction === 'inbound',
                      'bg-red-400 bg-opacity-30 text-red-100': msg.classification === 'CANCELLED' && msg.direction !== 'inbound',
                      'bg-red-100 text-red-700': msg.classification === 'CANCELLED' && msg.direction === 'inbound',
                    }">{{ msg.classification }}</span>
                </div>
                <div class="px-1 mt-0.5" :class="msg.direction === 'inbound' ? 'text-left' : 'text-right'">
                  <span v-if="msg.timestamp" class="text-xs text-gray-400">{{ formatSmsTimestamp(msg.timestamp) }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-center text-gray-400 text-xs py-6">No previous messages</div>
        </div>
        <!-- Compose bar -->
        <div class="border-t border-gray-200 p-3 flex-shrink-0 bg-white rounded-b-xl">
          <div class="flex items-end space-x-2">
            <div class="flex-1 rounded-full bg-gray-100 border border-gray-200 px-4 py-2">
              <textarea v-model="smsModalMessage" placeholder="iMessage" rows="1" class="w-full bg-transparent text-sm text-gray-700 outline-none resize-none leading-5" style="max-height: 80px;" @input="$event.target.style.height = 'auto'; $event.target.style.height = $event.target.scrollHeight + 'px';"></textarea>
            </div>
            <button @click="sendModalSms()" :disabled="smsModalSending || !smsModalMessage.trim()" class="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center flex-shrink-0 mb-0.5 disabled:opacity-30">
              <i v-if="smsModalSending" class="fa-solid fa-spinner fa-spin text-xs"></i>
              <i v-else class="fa-solid fa-arrow-up text-xs font-bold"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== ADD PROJECT MODAL ==================== -->
    <div v-if="showAddProject" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-gray-900 bg-opacity-50" @click="showAddProject = false"></div>
      <div class="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 relative z-10 flex flex-col" style="height: 80vh; overflow-y: auto">
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 class="text-lg font-bold text-gray-900">New Project</h3>
          <button @click="showAddProject = false" class="text-gray-400 hover:text-gray-600"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <div class="flex-1 overflow-y-auto px-6 py-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="sm:col-span-2">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Project Name *</label>
              <input type="text" v-model="newProject.projectName" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400" />
            </div>
            <div class="sm:col-span-2">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Customer *</label>
              <div v-if="!showNewCustomerInput" class="relative">
                <div class="flex gap-2">
                  <div class="relative flex-1">
                    <input
                      type="text"
                      v-model="customerSearch"
                      @focus="showCustomerDropdown = true"
                      @input="showCustomerDropdown = true"
                      placeholder="Search existing customers..."
                      class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400"
                      :class="newProject.customerId ? 'bg-green-50 border-green-300' : ''"
                    />
                    <div v-if="showCustomerDropdown && filteredCustomerList.length > 0" class="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      <div
                        v-for="c in filteredCustomerList"
                        :key="c.id"
                        @click="selectCustomer(c)"
                        class="px-3 py-2 text-sm text-gray-700 hover:bg-indigo-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        {{ c.company }}
                      </div>
                    </div>
                    <div v-if="showCustomerDropdown && customerSearch && filteredCustomerList.length === 0" class="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-400 text-center">
                      No customers found
                    </div>
                  </div>
                  <button type="button" @click="showNewCustomerInput = true; customerSearch = ''; newProject.customerId = ''; newProject.company = ''"
                    class="flex-shrink-0 h-10 px-3 rounded-lg text-xs font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors whitespace-nowrap">
                    <i class="fa-solid fa-plus mr-1"></i>New
                  </button>
                </div>
                <p v-if="newProject.customerId" class="text-xs text-green-600 mt-1 text-left"><i class="fa-solid fa-check mr-0.5"></i>{{ newProject.company }}</p>
                <div v-if="showCustomerDropdown" class="fixed inset-0 z-10" @click="showCustomerDropdown = false"></div>
              </div>
              <div v-else>
                <div class="flex gap-2">
                  <input
                    type="text"
                    v-model="newCustomerName"
                    placeholder="New company name..."
                    class="flex-1 text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400"
                  />
                  <button type="button" @click="showNewCustomerInput = false; newCustomerName = ''"
                    class="flex-shrink-0 h-10 px-3 rounded-lg text-xs font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors">
                    <i class="fa-solid fa-arrow-left mr-1"></i>Back
                  </button>
                </div>
                <p class="text-xs text-gray-400 mt-1 text-left">A new customer will be created</p>
              </div>
            </div>
            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Primary Contact</label>
              <input type="text" v-model="newProject.contact" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400" />
            </div>
            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Phone</label>
              <input type="text" v-model="newProject.phone" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400" />
            </div>
            <div class="sm:col-span-2">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Email</label>
              <input type="email" v-model="newProject.email" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400" />
            </div>
            <div class="sm:col-span-2 relative">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Address</label>
              <input type="text" v-model="newProject.formattedAddress" @input="debounceAddressGeocode()" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400" placeholder="Start typing an address..." />
              <div v-if="addressResults.length > 0" class="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                <div v-for="(result, idx) in addressResults" :key="idx" @click="selectAddress(result)" class="px-3 py-2 text-sm text-gray-700 hover:bg-indigo-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                  {{ result.place_name }}
                </div>
              </div>
            </div>
            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Region</label>
              <select v-model="newProject.region" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400 bg-white">
                <option value="">Select region</option>
                <option v-for="r in availableRegions" :key="r.id" :value="r.name">{{ r.name }}</option>
              </select>
            </div>
            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">PO Number</label>
              <input type="text" v-model="newProject.poNumber" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400" />
            </div>
            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Secondary Contact</label>
              <input type="text" v-model="newProject.secondaryContact" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400" />
            </div>
            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Secondary Phone</label>
              <input type="text" v-model="newProject.secondaryPhone" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400" />
            </div>
            <div class="sm:col-span-2">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Secondary Email</label>
              <input type="email" v-model="newProject.secondaryEmail" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400" />
            </div>
            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Call Frequency</label>
              <select v-model="newProject.callFrequency" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400 bg-white">
                <option value="">-- Select --</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="monthly">Monthly</option>
                <option value="as needed">As Needed</option>
              </select>
            </div>
            <div class="sm:col-span-2">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Team Leaders</label>
              <div class="flex flex-wrap gap-1.5 mb-1.5">
                <span v-for="tl in (newProject.teamLeaders || [])" :key="tl.id" class="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-xs font-medium">
                  {{ tl.name }}
                  <button @click="removeTeamLeaderFromJob(newProject, tl.id)" class="ml-1 text-yellow-600 hover:text-yellow-800">&times;</button>
                </span>
              </div>
              <select v-if="availableTeamLeadersForJob(newProject).length > 0" @change="addTeamLeaderToJob(newProject, $event)" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none bg-white">
                <option value="">Add team leader...</option>
                <option v-for="tl in availableTeamLeadersForJob(newProject)" :key="tl.id" :value="tl.id">{{ teamLeaderLabel(tl) }}</option>
              </select>
              <p class="text-xs text-gray-400 mt-1 text-left">Applied by default to new jobs in this project</p>
            </div>
          </div>
        </div>
        <div class="px-6 py-4 border-t border-gray-200 flex items-center space-x-3 flex-shrink-0">
          <button @click="saveNewProject()" :disabled="newProjectSaving" class="flex-1 bg-indigo-600 text-white rounded-lg py-3 text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
            <i v-if="newProjectSaving" class="fa-solid fa-spinner fa-spin mr-2"></i>
            {{ newProjectSaving ? 'Creating...' : 'Create Project' }}
          </button>
          <button @click="showAddProject = false" class="flex-1 border border-gray-300 text-gray-700 rounded-lg py-3 text-sm font-medium hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>

    <!-- ==================== EDIT PROJECT MODAL ==================== -->
    <div v-if="showEditProject" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-gray-900 bg-opacity-50" @click="showEditProject = false"></div>
      <div class="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 relative z-10 flex flex-col" style="height: 80vh; overflow-y: auto">
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 class="text-lg font-bold text-gray-900">Edit Project</h3>
          <button @click="showEditProject = false" class="text-gray-400 hover:text-gray-600"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <div class="flex-1 overflow-y-auto px-6 py-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="sm:col-span-2">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Project Name</label>
              <input type="text" v-model="editProject.projectName" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400" />
            </div>
            <div class="sm:col-span-2">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Company</label>
              <div v-if="!showEditNewCompany" class="relative">
                <div class="flex gap-2">
                  <div class="relative flex-1">
                    <input
                      type="text"
                      v-model="editCompanySearch"
                      @focus="showEditCompanyDropdown = true"
                      @input="showEditCompanyDropdown = true"
                      placeholder="Search companies..."
                      class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400"
                      :class="editProject.company && editCompanySearch === editProject.company ? 'bg-green-50 border-green-300' : ''"
                    />
                    <div v-if="showEditCompanyDropdown && editFilteredCompanies.length > 0" class="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      <div
                        v-for="c in editFilteredCompanies"
                        :key="c.id"
                        @click="editProject.company = c.company; editProject.customerId = c.id; editCompanySearch = c.company; showEditCompanyDropdown = false"
                        class="px-3 py-2 text-sm text-gray-700 hover:bg-indigo-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        {{ c.company }}
                      </div>
                    </div>
                    <div v-if="showEditCompanyDropdown && editCompanySearch && editFilteredCompanies.length === 0" class="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-400 text-center">
                      No companies found
                    </div>
                    <div v-if="showEditCompanyDropdown" class="fixed inset-0 z-10" @click="showEditCompanyDropdown = false"></div>
                  </div>
                  <button type="button" @click="showEditNewCompany = true; editCompanySearch = ''"
                    class="flex-shrink-0 h-10 px-3 rounded-lg text-xs font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors whitespace-nowrap">
                    <i class="fa-solid fa-plus mr-1"></i>New
                  </button>
                </div>
              </div>
              <div v-else>
                <div class="flex gap-2">
                  <input
                    type="text"
                    v-model="editNewCompanyName"
                    placeholder="New company name..."
                    class="flex-1 text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400"
                  />
                  <button type="button" @click="showEditNewCompany = false; editNewCompanyName = ''; editCompanySearch = editProject.company || ''"
                    class="flex-shrink-0 h-10 px-3 rounded-lg text-xs font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors">
                    <i class="fa-solid fa-arrow-left mr-1"></i>Back
                  </button>
                </div>
                <p class="text-xs text-gray-400 mt-1 text-left">A new customer will be created</p>
              </div>
            </div>
            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Primary Contact</label>
              <input type="text" v-model="editProject.contact" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400" />
            </div>
            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Phone</label>
              <input type="text" v-model="editProject.phone" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400" />
            </div>
            <div class="sm:col-span-2">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Email</label>
              <input type="email" v-model="editProject.email" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400" />
            </div>
            <div class="sm:col-span-2 relative">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Address</label>
              <input type="text" v-model="editProject.formattedAddress" @input="debounceEditAddressGeocode()" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400" placeholder="Start typing an address..." />
              <div v-if="addressResults.length > 0 && showEditProject" class="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                <div v-for="(result, idx) in addressResults" :key="idx" @click="selectEditAddress(result)" class="px-3 py-2 text-sm text-gray-700 hover:bg-indigo-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                  {{ result.place_name }}
                </div>
              </div>
            </div>
            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Region</label>
              <select v-model="editProject.region" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400 bg-white">
                <option value="">Select region</option>
                <option v-for="r in availableRegions" :key="r.id" :value="r.name">{{ r.name }}</option>
              </select>
            </div>
            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">PO Number</label>
              <input type="text" v-model="editProject.poNumber" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400" />
            </div>
            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Secondary Contact</label>
              <input type="text" v-model="editProject.secondaryContact" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400" />
            </div>
            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Secondary Phone</label>
              <input type="text" v-model="editProject.secondaryPhone" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400" />
            </div>
            <div class="sm:col-span-2">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Secondary Email</label>
              <input type="email" v-model="editProject.secondaryEmail" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400" />
            </div>
            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Call Frequency</label>
              <select v-model="editProject.callFrequency" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400 bg-white">
                <option value="">-- Select --</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="monthly">Monthly</option>
                <option value="as needed">As Needed</option>
              </select>
            </div>
            <div class="sm:col-span-2">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Team Leaders</label>
              <div class="flex flex-wrap gap-1.5 mb-1.5">
                <span v-for="tl in (editProject.teamLeaders || [])" :key="tl.id" class="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-xs font-medium">
                  {{ tl.name }}
                  <button @click="removeTeamLeaderFromJob(editProject, tl.id)" class="ml-1 text-yellow-600 hover:text-yellow-800">&times;</button>
                </span>
              </div>
              <select v-if="availableTeamLeadersForJob(editProject).length > 0" @change="addTeamLeaderToJob(editProject, $event)" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none bg-white">
                <option value="">Add team leader...</option>
                <option v-for="tl in availableTeamLeadersForJob(editProject)" :key="tl.id" :value="tl.id">{{ teamLeaderLabel(tl) }}</option>
              </select>
              <p class="text-xs text-gray-400 mt-1 text-left">Applied by default to new jobs in this project. Existing jobs are unchanged.</p>
            </div>
            <div class="sm:col-span-2">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Notes</label>
              <textarea v-model="editProject.notes" rows="3" class="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-400"></textarea>
            </div>
          </div>
        </div>
        <div class="px-6 py-4 border-t border-gray-200 flex items-center space-x-3 flex-shrink-0">
          <button @click="saveEditProject()" :disabled="editProjectSaving" class="flex-1 bg-indigo-600 text-white rounded-lg py-3 text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
            <i v-if="editProjectSaving" class="fa-solid fa-spinner fa-spin mr-2"></i>
            {{ editProjectSaving ? 'Saving...' : 'Save Changes' }}
          </button>
          <button @click="showEditProject = false" class="flex-1 border border-gray-300 text-gray-700 rounded-lg py-3 text-sm font-medium hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>

    <!-- ==================== ADD JOB MODAL ==================== -->
    <div v-if="showAddJob" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-gray-900 bg-opacity-50" @click="showAddJob = false"></div>
      <div class="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 relative z-10 flex flex-col" style="height: 80vh; overflow-y: auto">
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 class="text-lg font-bold text-gray-900">New Job</h3>
          <button @click="showAddJob = false" class="text-gray-400 hover:text-gray-600"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <div class="flex-1 overflow-y-auto px-6 py-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <!-- Company picker -->
            <div class="sm:col-span-2">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Company *</label>
              <div v-if="!showJobNewCompany" class="relative">
                <div class="flex gap-2">
                  <div class="relative flex-1">
                    <input
                      type="text"
                      v-model="jobCompanySearch"
                      @focus="showJobCompanyDropdown = true"
                      @input="showJobCompanyDropdown = true; jobSelectedCompany = ''; jobSelectedProject = null"
                      placeholder="Search companies..."
                      class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400"
                      :class="jobSelectedCompany ? 'bg-green-50 border-green-300' : ''"
                    />
                    <div v-if="showJobCompanyDropdown && jobFilteredCompanies.length > 0" class="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      <div
                        v-for="c in jobFilteredCompanies"
                        :key="c.id"
                        @click="jobSelectedCompany = c.company; jobCompanySearch = c.company; showJobCompanyDropdown = false; jobSelectedProject = null"
                        class="px-3 py-2 text-sm text-gray-700 hover:bg-indigo-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        {{ c.company }}
                      </div>
                    </div>
                    <div v-if="showJobCompanyDropdown && jobCompanySearch && jobFilteredCompanies.length === 0" class="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-400 text-center">
                      No companies found
                    </div>
                    <div v-if="showJobCompanyDropdown" class="fixed inset-0 z-10" @click="showJobCompanyDropdown = false"></div>
                  </div>
                  <button type="button" @click="showJobNewCompany = true; jobCompanySearch = ''; jobSelectedCompany = ''; jobSelectedProject = null"
                    class="flex-shrink-0 h-10 px-3 rounded-lg text-xs font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors whitespace-nowrap">
                    <i class="fa-solid fa-plus mr-1"></i>New
                  </button>
                </div>
              </div>
              <div v-else>
                <div class="flex gap-2">
                  <input
                    type="text"
                    v-model="jobNewCompanyName"
                    placeholder="New company name..."
                    class="flex-1 text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400"
                  />
                  <button type="button" @click="showJobNewCompany = false; jobNewCompanyName = ''"
                    class="flex-shrink-0 h-10 px-3 rounded-lg text-xs font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors">
                    <i class="fa-solid fa-arrow-left mr-1"></i>Back
                  </button>
                </div>
                <p class="text-xs text-gray-400 mt-1 text-left">A new customer will be created</p>
              </div>
            </div>

            <!-- Project picker (shown after company selected) -->
            <div v-if="jobSelectedCompany && !showJobNewCompany" class="sm:col-span-2">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Project *</label>
              <div v-if="jobProjectsForCompany.length === 0" class="text-sm text-gray-400 py-2">No projects for this company</div>
              <select v-else v-model="jobSelectedProject" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400 bg-white">
                <option :value="null">-- Select project --</option>
                <option v-for="p in jobProjectsForCompany" :key="p.id || p.projectId" :value="p">
                  {{ p.projectName || 'Untitled' }} — {{ p.formattedAddress || 'No address' }}
                </option>
              </select>
            </div>

            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Date &amp; Time</label>
              <flat-pickr v-model="newJob.date" :config="dateTimeConfig" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none" />
            </div>
            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Status</label>
              <select v-model="newJob.status" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none bg-white">
                <option>ON-CALL</option><option>FIRM</option><option>COMMITTED</option>
              </select>
            </div>
            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Job Contact</label>
              <input type="text" v-model="newJob.jobContact" placeholder="Job-specific contact" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none" />
            </div>
            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Job Phone</label>
              <input type="text" v-model="newJob.jobPhone" placeholder="Job-specific mobile" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none" />
            </div>
            <div class="sm:col-span-2">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Job Email</label>
              <input type="text" v-model="newJob.jobEmail" placeholder="Job-specific email" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none" />
            </div>
            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Tester</label>
              <select v-model="newJob.contractor" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none bg-white">
                <option :value="null">-- Select --</option>
                <option v-for="c in contractors" :key="c.id" :value="c">{{ c.name }}</option>
              </select>
            </div>
            <div class="sm:col-span-2">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Team Leaders</label>
              <div class="flex flex-wrap gap-1.5 mb-1.5">
                <span v-for="tl in (newJob.teamLeaders || [])" :key="tl.id" class="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-xs font-medium">
                  {{ tl.name }}
                  <button @click="removeTeamLeaderFromJob(newJob, tl.id)" class="ml-1 text-yellow-600 hover:text-yellow-800">&times;</button>
                </span>
              </div>
              <select v-if="availableTeamLeadersForJob(newJob).length > 0" @change="addTeamLeaderToJob(newJob, $event)" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none bg-white">
                <option value="">Add team leader...</option>
                <option v-for="tl in availableTeamLeadersForJob(newJob)" :key="tl.id" :value="tl.id">{{ teamLeaderLabel(tl) }}</option>
              </select>
            </div>
            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Region</label>
              <select v-model="newJob.region" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none focus:border-indigo-400 bg-white">
                <option value="">Select region</option>
                <option v-for="r in availableRegions" :key="r.id" :value="r.name">{{ r.name }}</option>
              </select>
            </div>
            <div v-if="customFieldsEnabled">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Service</label>
              <select :value="jobService(newJob)" @change="setJobService(newJob, $event.target.value)" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none bg-white">
                <option v-for="s in customServices" :key="s.key" :value="s.key">{{ s.label }}</option>
              </select>
            </div>
            <template v-if="!customFieldsEnabled">
              <div>
                <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Mix Code</label>
                <input type="text" v-model="newJob.mixCode" placeholder="e.g. N32" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none" />
              </div>
              <div>
                <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Meters (m&sup3;)</label>
                <input type="text" v-model="newJob.meters" placeholder="e.g. 4" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none" />
              </div>
            </template>
            <div>
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Duration (min)</label>
              <input type="number" v-model="newJob.duration" placeholder="e.g. 60" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none" />
            </div>
            <div class="sm:col-span-2">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Requirements</label>
              <input type="text" v-model="newJob.requirements" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none" />
            </div>
            <div v-if="!customFieldsEnabled" class="sm:col-span-2">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Pour Location</label>
              <input type="text" v-model="newJob.pourLocation" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none" />
            </div>
            <custom-fields-input v-if="customFieldsEnabled" :job="newJob" :fields="jobCardFields(newJob)" input-class="border border-gray-200 bg-white" />
            <div class="sm:col-span-2">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Notes for Tester</label>
              <input type="text" v-model="newJob.notesForTester" class="w-full text-sm border border-gray-200 rounded-lg px-3 h-10 outline-none" />
            </div>
            <div class="sm:col-span-2">
              <label class="text-xs font-medium text-gray-600 mb-1.5 block text-left">Notes</label>
              <textarea v-model="newJob.notes" rows="2" class="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none"></textarea>
            </div>
            <div class="flex items-center">
              <label class="text-xs font-medium text-gray-600 mr-3">Flexible</label>
              <div @click="newJob.flexible = !newJob.flexible" :class="newJob.flexible ? 'bg-green-500' : 'bg-gray-300'" class="relative w-10 h-5 rounded-full cursor-pointer transition-colors">
                <div :class="newJob.flexible ? 'translate-x-5' : 'translate-x-0'" class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform"></div>
              </div>
            </div>
          </div>
        </div>
        <div class="px-6 py-4 border-t border-gray-200 flex items-center space-x-3 flex-shrink-0">
          <button @click="saveNewJob()" :disabled="jobSaving || (!jobSelectedProject && !showJobNewCompany)" class="flex-1 bg-indigo-600 text-white rounded-lg py-3 text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
            <i v-if="jobSaving" class="fa-solid fa-spinner fa-spin mr-2"></i>
            {{ jobSaving ? 'Saving...' : 'Create Job' }}
          </button>
          <button @click="showAddJob = false" class="flex-1 border border-gray-300 text-gray-700 rounded-lg py-3 text-sm font-medium hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>

    <!-- ==================== CLIENT LINK MODAL ==================== -->
    <div v-if="linkModal" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-gray-900 bg-opacity-50" @click="linkModal = false"></div>
      <div class="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4 relative z-10">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-bold text-gray-900">Client Link</h3>
          <button @click="linkModal = false" class="text-gray-400 hover:text-gray-600"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <div v-if="linkJob" class="mb-3 text-sm text-gray-500">
          <span class="font-medium text-gray-700">{{ linkJob.company || selectedProject.company }}</span>
          <span v-if="linkJob.formattedAddress || selectedProject.formattedAddress"> &mdash; {{ linkJob.formattedAddress || selectedProject.formattedAddress }}</span>
        </div>
        <div class="flex items-center space-x-2 mb-4">
          <input type="text" readonly :value="linkUrl" class="flex-1 text-sm border border-gray-200 rounded-lg px-3 h-10 bg-gray-50 outline-none text-gray-700 select-all" @click="$event.target.select()" />
          <button @click="copyLink()" :class="linkCopied ? 'bg-green-600 text-white' : 'bg-indigo-600 text-white hover:bg-indigo-700'" class="px-4 h-10 rounded-lg text-sm font-medium transition-colors flex-shrink-0">
            <i :class="linkCopied ? 'fa-solid fa-check' : 'fa-regular fa-copy'" class="mr-1"></i>
            {{ linkCopied ? 'Copied!' : 'Copy' }}
          </button>
        </div>
        <p class="text-xs text-gray-400">Share this link with the client to let them view job details and live tracking.</p>
      </div>
    </div>

    <!-- ==================== MOBILE PROJECTS DRAWER ==================== -->
    <div v-if="showMobileProjects" class="fixed inset-0 z-40 lg:hidden">
      <div class="absolute inset-0 bg-gray-900 bg-opacity-50" @click="showMobileProjects = false"></div>
      <div class="absolute inset-y-0 left-0 w-80 bg-white shadow-xl flex flex-col z-10">
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 class="text-sm font-bold text-gray-900">Projects</h3>
          <div class="flex items-center space-x-2">
            <button @click="openAddProject()" class="text-indigo-600 hover:text-indigo-800 p-1" title="New Project"><i class="fa-solid fa-folder-plus text-sm"></i></button>
            <button @click="showMobileProjects = false" class="text-gray-400 hover:text-gray-600"><i class="fa-solid fa-xmark"></i></button>
          </div>
        </div>
        <div class="flex items-center px-3 py-2 border-b border-gray-100">
          <button @click="projectTab = 'current'" :class="projectTab === 'current' ? 'bg-gray-900 text-white' : 'text-gray-600'" class="px-3 py-1 rounded-md text-xs font-medium mr-1">Current</button>
          <button @click="projectTab = 'archived'" :class="projectTab === 'archived' ? 'bg-gray-900 text-white' : 'text-gray-600'" class="px-3 py-1 rounded-md text-xs font-medium">Archived</button>
        </div>
        <!-- Search field checkboxes -->
        <div class="flex items-center gap-3 px-3 py-2 border-b border-gray-100 text-xs text-gray-500">
          <label class="flex items-center gap-1 cursor-pointer"><input type="checkbox" v-model="searchFields.projectName" class="w-3 h-3 rounded" /> Name</label>
          <label class="flex items-center gap-1 cursor-pointer"><input type="checkbox" v-model="searchFields.client" class="w-3 h-3 rounded" /> Client</label>
          <label class="flex items-center gap-1 cursor-pointer"><input type="checkbox" v-model="searchFields.address" class="w-3 h-3 rounded" /> Address</label>
        </div>
        <div class="flex-1 overflow-y-auto">
          <div v-for="project in filteredProjects" :key="project.projectId"
            @click="selectProject(project); showMobileProjects = false"
            :class="['px-4 py-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50', selectedProjectId == project.projectId ? 'bg-indigo-50 border-l-4 border-l-indigo-600' : '']"
          >
            <p class="text-sm font-semibold text-gray-900 truncate">{{ project.projectName || 'Untitled' }}</p>
            <p class="text-xs text-gray-500 truncate">{{ project.company }}</p>
            <span class="text-xs text-gray-400">{{ getJobCount(project) }} jobs</span>
          </div>
        </div>
      </div>
    </div>

    <!-- SMS ALERT MODAL (auto-popup for CANCELLED/CHANGED) -->
    <div v-if="smsAlertModal && smsAlertJob" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-gray-900 bg-opacity-50" @click="dismissSmsAlert()"></div>
      <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <!-- Header -->
        <div class="px-6 pt-6 pb-4 text-center">
          <div class="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4"
            :class="smsAlertStatus === 'CANCELLED' ? 'bg-red-100' : 'bg-yellow-100'">
            <i class="text-2xl"
              :class="smsAlertStatus === 'CANCELLED' ? 'fa-solid fa-circle-xmark text-red-500' : 'fa-solid fa-circle-question text-yellow-500'"></i>
          </div>
          <h3 class="text-lg font-bold text-gray-900">
            {{ smsAlertStatus === 'CANCELLED' ? 'Job Cancelled via SMS' : 'Job Change Requested via SMS' }}
          </h3>
          <p class="text-sm text-gray-500 mt-1">A client has responded to their job SMS</p>
        </div>
        <!-- Job details -->
        <div class="px-6 pb-4">
          <div class="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-500">Company</span>
              <span class="font-medium text-gray-900">{{ smsAlertJob.company || '-' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Contact</span>
              <span class="font-medium text-gray-900">{{ smsAlertJob.jobContact || smsAlertJob.contact || '-' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Address</span>
              <span class="font-medium text-gray-900 text-right ml-4">{{ smsAlertJob.formattedAddress || '-' }}</span>
            </div>
            <div v-if="smsAlertJob.date" class="flex justify-between">
              <span class="text-gray-500">Date</span>
              <span class="font-medium text-gray-900">{{ formatDate(smsAlertJob.date) }}</span>
            </div>
            <div v-if="smsAlertJob.clientSmsMessage" class="pt-2 border-t border-gray-200">
              <span class="text-gray-500 block mb-1">Client Message</span>
              <p class="font-medium text-gray-900 italic">"{{ smsAlertJob.clientSmsMessage }}"</p>
            </div>
          </div>
        </div>
        <!-- Actions -->
        <div class="px-6 pb-6 flex gap-3">
          <button @click="dismissSmsAlert()" class="flex-1 rounded-lg border border-gray-300 text-gray-700 font-medium py-3 text-sm hover:bg-gray-50 transition">
            Dismiss
          </button>
          <button @click="viewAlertJob()" class="flex-1 rounded-lg font-medium py-3 text-sm text-white transition"
            :class="smsAlertStatus === 'CANCELLED' ? 'bg-red-500 hover:bg-red-600' : 'bg-yellow-500 hover:bg-yellow-600'">
            View Job
          </button>
        </div>
      </div>
    </div>

    <!-- TOAST -->
    <div v-if="toast" class="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg z-50">{{ toast }}</div>

    <!-- Change Resolve Modal -->
    <div v-if="changeResolveModal" class="w-full z-50 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50 flex items-center justify-center" style="backdrop-filter:blur(2px)">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
        <h3 class="text-lg font-bold text-gray-900 mb-4">Resolve Change Request</h3>
        <div v-if="changeResolveJob && changeResolveJob.clientSmsMessage" class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
          <p class="text-xs text-gray-500 mb-1">Client message</p>
          <p class="text-sm text-gray-800 italic">"{{ changeResolveJob.clientSmsMessage }}"</p>
        </div>
        <textarea v-model="changeResolveNote" rows="3" class="w-full border border-gray-300 rounded-lg p-3 text-sm outline-none focus:ring-2 focus:ring-primary mb-4" placeholder="Resolution note (optional)"></textarea>
        <div class="flex space-x-3">
          <button @click="changeResolveModal = false; changeResolveJob = null; changeResolveNote = ''" class="flex-1 h-10 rounded-lg border border-gray-300 text-gray-600 font-medium hover:bg-gray-50">Cancel</button>
          <button @click="resolveChangeRequest()" class="flex-1 h-10 rounded-lg bg-primary text-white font-medium hover:opacity-90">Mark as Resolved</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavMenu from "@/components/headers/navMenu";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import {
  postFire,
  putFire,
  deleteFire,
  setFire,
  getContractors,
  getCustomers,
  getNextJobNumber,
  getTeamLeaders,
  getTeamMembers,
  createTeamLeaderInvite,
} from "../firestore/utils";
import { db, auth, getTimeStamp } from "../firebase";
import { collection, getDocs, query, where, doc, getDoc, onSnapshot, writeBatch, orderBy } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { detectRegion } from "../utils/regionUtils";
import CustomFieldsInput from "@/components/fields/CustomFieldsInput";
import CustomFieldsDisplay from "@/components/fields/CustomFieldsDisplay";
import {
  DEFAULT_SERVICE,
  activeServices,
  allFieldKeys,
  applyCustomFieldTokens,
  fieldsFor,
  loadDefinitions,
  resolveService,
  smsShortcodesForJob,
  templatesForJob,
} from "../services/customFields";

const MAPBOX_TOKEN =
  "pk.eyJ1IjoicHJlbWFya2V0aG9tZXMiLCJhIjoiY21qamRkb3BsMGU2NzNlb3k4MWs2Y3NtaCJ9.JxBalubMXzSxJWsc2eTWrw";
const API_BASE =
  "https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets";

// Extract lat/lng from a job or project, handling various field structures
function getJobCoords(item) {
  if (!item) return null;
  if (item.address && item.address.latitude && item.address.longitude) {
    return { lat: item.address.latitude, lng: item.address.longitude };
  }
  if (item.geopoint) {
    const gp = item.geopoint;
    if (gp.latitude && gp.longitude) return { lat: gp.latitude, lng: gp.longitude };
    if (gp._lat && gp._long) return { lat: gp._lat, lng: gp._long };
    if (gp.Pc && gp.Vc) return { lat: gp.Pc, lng: gp.Vc };
  }
  if (item.latitude && item.longitude) return { lat: item.latitude, lng: item.longitude };
  if (item.lat && item.lng) return { lat: item.lat, lng: item.lng };
  return null;
}

// Distance between two points in meters (Haversine)
function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default {
  name: "JobsEnterprise",
  components: { NavMenu, flatPickr, CustomFieldsInput, CustomFieldsDisplay },
  data() {
    return {
      // Per-service custom fields (null until loaded; feature is inert while
      // `enabled` is false)
      customFieldDefs: null,

      // View state
      projectTab: "current",
      contextTab: "jobs",
      selectedProjectId: null,
      selectedJobId: null,
      jobViewMode: "grid",
      jobFilterFrom: "",
      jobFilterTo: "",

      // Data
      allProjects: [],
      allJobs: {},
      contractors: [],
      teamLeaders: [],
      teamMembers: [],
      teamLeaderFilter: 'all',
      loading: true,

      // Search
      projectSearch: "",
      searchFields: { address: true, projectName: true, client: true },

      // Address geocoding
      addressResults: [],
      addressGeoTimer: null,

      // Regions
      availableRegions: [],

      // Project notes editing
      editingProjectNotes: false,
      projectNotesText: "",

      // Job editing
      jobSaving: false,
      showCompleted: false,
      completedJobsMap: {},       // projectId -> [jobs]
      completedJobCounts: {},     // projectId -> count
      completedJobsLoading: false,
      completedJobsLoaded: {},    // projectId -> true if loaded

      // Change resolve modal
      changeResolveModal: false,
      changeResolveJob: null,
      changeResolveNote: '',

      // Add Job modal
      showAddJob: false,
      newJob: {
        date: "",
        status: "ON-CALL",
        contractor: null,
        region: "",
        mixCode: "",
        meters: "",
        duration: "",
        flexible: false,
        requirements: "",
        pourLocation: "",
        notesForTester: "",
        notes: "",
        jobContact: "",
        jobPhone: "",
        jobEmail: "",
        teamLeaders: [],
        teamLeaderIds: [],
      },
      // Add Job — company/project picker (used when no project pre-selected)
      jobCompanySearch: '',
      showJobCompanyDropdown: false,
      jobSelectedCompany: '',
      jobSelectedProject: null,
      showJobNewCompany: false,
      jobNewCompanyName: '',

      // Client Link modal
      linkModal: false,
      linkJob: null,
      linkUrl: "",
      linkCopied: false,

      // SMS context panel
      smsHistory: [],
      smsLoading: false,
      smsReplyText: "",

      // SMS send modal
      smsModal: false,
      smsModalJob: null,
      smsModalPhone: "",
      smsModalMessage: "",
      smsModalSending: false,
      smsModalHistory: [],
      smsModalJobDirty: false,
      smsModalJobSaving: false,
      smsModalJobSaved: false,
      smsTemplates: [],
      selectedSmsTemplateId: "",

      // Map
      map: null,
      mapMarkers: [],
      testerLocations: {},
      testerPollTimer: null,

      // Test reports
      jobTestReports: {},
      testReportFiles: {},
      unsubTestReports: null,

      // Delete
      deleteTarget: null,
      deleteConfirm: false,

      // Close project
      closeProjectTarget: null,
      closingProject: false,

      // Reopen project
      reopenProjectTarget: null,
      reopeningProject: false,

      // Settings menu
      showSettingsMenu: false,

      // Auto archive
      autoArchiveModal: false,
      autoArchiveTab: 'archive',
      autoArchiveDays: 30,
      autoArchiveRunning: false,
      autoArchiveDone: false,
      autoArchiveCount: 0,
      autoArchiveProgress: 0,
      autoArchiveTotal: 0,
      autoArchiveLog: [],

      // Mobile
      showMobileProjects: false,

      // Add Project modal
      showAddProject: false,
      newProject: {
        projectName: "",
        company: "",
        contact: "",
        phone: "",
        email: "",
        formattedAddress: "",
        region: "",
        poNumber: "",
        secondaryContact: "",
        secondaryPhone: "",
        secondaryEmail: "",
        callFrequency: "",
        teamLeaders: [],
        teamLeaderIds: [],
      },
      newProjectSaving: false,
      allCustomers: [],
      customerSearch: '',
      showCustomerDropdown: false,
      showNewCustomerInput: false,
      newCustomerName: '',

      // Edit Project modal
      showEditProject: false,
      editProject: {
        projectName: "",
        company: "",
        contact: "",
        phone: "",
        email: "",
        formattedAddress: "",
        region: "",
        poNumber: "",
        secondaryContact: "",
        secondaryPhone: "",
        secondaryEmail: "",
        callFrequency: "",
        notes: "",
        teamLeaders: [],
        teamLeaderIds: [],
      },
      editProjectSaving: false,
      editCompanySearch: '',
      showEditCompanyDropdown: false,
      showEditNewCompany: false,
      editNewCompanyName: '',

      // SMS alert modal (auto-popup for CANCELLED/CHANGED)
      smsAlertModal: false,
      smsAlertJob: null,
      smsAlertStatus: "",

      // Real-time job listener unsubscribe
      jobsUnsubscribe: null,

      // Toast
      toast: "",
      toastTimer: null,

      // flatpickr config
      dateTimeConfig: {
        enableTime: true,
        altFormat: "d-m-Y H:i",
        altInput: true,
        allowInput: false,
      },
      dateOnlyConfig: {
        enableTime: false,
        altFormat: "d-m-Y",
        altInput: true,
        allowInput: false,
      },
    };
  },

  computed: {
    customFieldsEnabled() {
      return !!(this.customFieldDefs && this.customFieldDefs.enabled);
    },
    customServices() {
      return activeServices(this.customFieldDefs);
    },

    currentUser() {
      return this.$store.state.currentUser;
    },
    isAdmin() {
      return this.currentUser && this.currentUser.role === 'admin';
    },
    isTeamLeader() {
      return this.currentUser && this.currentUser.isTeamLeader === true && this.currentUser.role !== 'admin';
    },

    smsShortcodes() {
      return [
        { code: '{recipientName}', label: 'Recipient name' },
        { code: '{company}', label: 'Company' },
        { code: '{project}', label: 'Project name' },
        { code: '{contact}', label: 'Primary contact' },
        { code: '{address}', label: 'Job address' },
        { code: '{date}', label: 'Job date' },
        { code: '{time}', label: 'Job time' },
        { code: '{meters}', label: 'Meters' },
        { code: '{link}', label: 'Client portal link' },
      ].concat(smsShortcodesForJob(this.customFieldDefs, this.smsModalJob));
    },

    // Service-locked templates only appear for a job of that service.
    visibleSmsTemplates() {
      return templatesForJob(this.customFieldDefs, this.smsTemplates, this.smsModalJob);
    },

    editFilteredCompanies() {
      if (!this.editCompanySearch) return this.allCustomers;
      const q = this.editCompanySearch.toLowerCase();
      return this.allCustomers.filter(c => (c.company || '').toLowerCase().includes(q));
    },
    jobFilteredCompanies() {
      if (!this.jobCompanySearch) return this.allCustomers;
      const q = this.jobCompanySearch.toLowerCase();
      return this.allCustomers.filter(c => (c.company || '').toLowerCase().includes(q));
    },
    jobProjectsForCompany() {
      if (!this.jobSelectedCompany) return [];
      const name = this.jobSelectedCompany.toLowerCase();
      return this.allProjects.filter(p => (p.company || '').toLowerCase() === name && p.closed !== true);
    },
    filteredCustomerList() {
      if (!this.customerSearch) return this.allCustomers;
      const q = this.customerSearch.toLowerCase();
      return this.allCustomers.filter(c => (c.company || '').toLowerCase().includes(q));
    },
    filteredProjects() {
      let projects = this.allProjects;
      if (this.projectTab === "current") {
        projects = projects.filter(
          (p) => p.closed !== true
        );
      } else {
        projects = projects.filter(
          (p) => p.closed === true
        );
      }
      if (this.projectSearch) {
        const s = this.projectSearch.toLowerCase();
        const sf = this.searchFields;
        projects = projects.filter((p) => {
          if (String(p.projectId || "").includes(s)) return true;
          if (sf.projectName && (p.projectName || "").toLowerCase().includes(s)) return true;
          if (sf.client && (p.company || "").toLowerCase().includes(s)) return true;
          if (sf.address && (p.formattedAddress || "").toLowerCase().includes(s)) return true;
          return false;
        });
      }
      return projects;
    },

    // KPI stats for dashboard landing
    kpiStats() {
      const allProjects = this.allProjects;
      const openProjects = allProjects.filter(
        (p) => p.closed !== true
      ).length;
      const closedProjects = allProjects.filter(
        (p) => p.closed === true
      ).length;
      let onCall = 0, firm = 0, cancelled = 0, completed = 0, total = 0;
      Object.values(this.allJobs).forEach((jobs) => {
        jobs.forEach((j) => {
          total++;
          if (j.status === "ON-CALL") onCall++;
          else if (j.status === "FIRM") firm++;
          else if (j.status === "CANCELLED") cancelled++;
          else if (j.status === "COMPLETED") completed++;
        });
      });
      return { openProjects, closedProjects, onCall, firm, cancelled, completed, total };
    },

    selectedProject() {
      if (!this.selectedProjectId) return null;
      return (
        this.allProjects.find(
          (p) => p.projectId == this.selectedProjectId
        ) || null
      );
    },

    selectedProjectJobs() {
      const pid = this.selectedProjectId;
      if (!pid) return [];
      let jobs = this.allJobs[String(pid)] || [];
      // Team leaders see jobs allocated to their team, plus any job where they
      // are the assigned tester (a leader can also work jobs themselves).
      if (this.isTeamLeader && this.currentUser) {
        const uid = this.currentUser.id;
        jobs = jobs.filter(j =>
          (j.teamLeaderIds && j.teamLeaderIds.includes(uid)) ||
          (j.contractor && j.contractor.id === uid)
        );
      }
      return [...jobs].sort((a, b) => {
        const da = this.toDate(a.date);
        const db2 = this.toDate(b.date);
        return db2 - da;
      });
    },

    currentJobs() {
      const jobs = this.selectedProjectJobs.filter(
        (j) => j.status !== "COMPLETED" && j.status !== "CANCELLED"
      );
      // Sort by next start date first (ascending)
      return [...jobs].sort((a, b) => {
        const da = this.toDate(a.date);
        const db2 = this.toDate(b.date);
        return da - db2;
      });
    },

    filteredCurrentJobs() {
      let jobs = this.currentJobs;
      if (this.jobFilterFrom) {
        const from = this.$moment(this.jobFilterFrom).startOf("day").valueOf();
        jobs = jobs.filter((j) => this.toDate(j.date) >= from);
      }
      if (this.jobFilterTo) {
        const to = this.$moment(this.jobFilterTo).endOf("day").valueOf();
        jobs = jobs.filter((j) => this.toDate(j.date) <= to);
      }
      // Team leader tester filter
      if (this.isTeamLeader && this.teamLeaderFilter !== 'all') {
        if (this.teamLeaderFilter === 'unassigned') {
          jobs = jobs.filter(j => !j.contractor);
        } else {
          jobs = jobs.filter(j => j.contractor && j.contractor.id === this.teamLeaderFilter);
        }
      }
      return jobs;
    },

    completedJobs() {
      const pid = String(this.selectedProjectId);
      const loaded = this.completedJobsMap[pid] || [];
      // Also include any in allJobs that became completed via inline status change
      const inlineCompleted = this.selectedProjectJobs.filter(
        (j) => j.status === "COMPLETED" || j.status === "CANCELLED"
      );
      // Merge without duplicates
      const ids = new Set(loaded.map((j) => j.id));
      const merged = [...loaded];
      inlineCompleted.forEach((j) => {
        if (!ids.has(j.id)) merged.push(j);
      });
      return merged.sort((a, b) => this.toDate(b.date) - this.toDate(a.date));
    },

    selectedJob() {
      if (!this.selectedJobId) return null;
      return (
        this.selectedProjectJobs.find(
          (j) => j.id === this.selectedJobId
        ) || null
      );
    },

    selectedJobTester() {
      const job = this.selectedJob;
      if (!job || !job.contractor) return null;
      const contractor = this.contractors.find(
        (c) => c.id === (job.contractor.id || job.contractor)
      );
      return contractor || job.contractor;
    },

    projectTesterIds() {
      const ids = new Set();
      (this.allJobs[String(this.selectedProjectId)] || []).forEach((j) => {
        if (j.contractor && j.contractor.id) ids.add(j.contractor.id);
      });
      return [...ids];
    },

    mapStats() {
      const jobs = this.filteredCurrentJobs;
      let enRoute = 0, arrived = 0, testing = 0, completed = 0;
      let testersOnline = 0;
      const seenTesters = new Set();
      jobs.forEach((j) => {
        const stage = this.getJobStageForMap(j);
        if (stage === 'en_route') enRoute++;
        else if (stage === 'arrived') arrived++;
        else if (stage === 'testing') testing++;
        else if (stage === 'completed') completed++;
        if (j.contractor && j.contractor.id && !seenTesters.has(j.contractor.id)) {
          seenTesters.add(j.contractor.id);
          if (this.testerLocations[j.contractor.id]) testersOnline++;
        }
      });
      return { total: jobs.length, enRoute, arrived, testing, completed, testersOnline };
    },
  },

  watch: {
    selectedProjectId(newVal) {
      this.selectedJobId = null;
      this.smsHistory = [];
      this.editingProjectNotes = false;
      this.contextTab = "jobs";
      this.showCompleted = false;
      if (newVal) {
        this.loadCompletedJobs(newVal);
      }
    },

    selectedJobId(newVal) {
      if (newVal) {
        this.loadSmsHistory(newVal);
      } else {
        this.smsHistory = [];
      }
    },

    contextTab(newVal) {
      if (newVal === "map" && this.selectedProject) {
        this.$nextTick(() => this.initMap());
      }
    },

    jobSelectedProject(proj) {
      if (proj && proj.region) {
        this.newJob.region = proj.region;
      }
      this.applyProjectTeamLeaders(this.newJob, proj);
    },
  },

  methods: {
    // ==================== CUSTOM FIELD METHODS ====================
    // A job with no `serviceType` is concrete testing — no migration needed.
    jobService(job) {
      return resolveService(job);
    },
    // Definitions the job card should render for this job's service.
    jobCardFields(job) {
      return fieldsFor(this.customFieldDefs, resolveService(job), "jobCard");
    },
    defaultServiceKey() {
      const services = this.customServices;
      if (!services.length) return DEFAULT_SERVICE;
      return services.some((s) => s.key === DEFAULT_SERVICE)
        ? DEFAULT_SERVICE
        : services[0].key;
    },
    // Values to merge into a job write payload. Returns {} while the feature is
    // off so saves stay byte-for-byte identical to today.
    customFieldPayload(job) {
      if (!this.customFieldsEnabled) return {};
      const serviceType = resolveService(job);
      const payload = { serviceType };
      fieldsFor(this.customFieldDefs, serviceType).forEach((f) => {
        const v = job[f.key];
        if (v === undefined || v === null) {
          payload[f.key] = f.type === "checkbox" ? false : "";
        } else {
          payload[f.key] = v;
        }
      });
      return payload;
    },
    setJobService(job, serviceKey) {
      this.$set(job, "serviceType", serviceKey);
    },
    // Seeds a fresh job object with the service's fields blanked out.
    seedCustomFields(target, serviceKey, source) {
      if (!this.customFieldsEnabled) return;
      this.$set(target, "serviceType", serviceKey);
      fieldsFor(this.customFieldDefs, serviceKey).forEach((f) => {
        const v = source ? source[f.key] : undefined;
        this.$set(
          target,
          f.key,
          v === undefined || v === null ? (f.type === "checkbox" ? false : "") : v
        );
      });
    },

    // ==================== TEAM LEADER METHODS ====================
    // Region helps the admin pick whoever is closest to the job
    teamLeaderLabel(tl) {
      return tl.region ? `${tl.name} (${tl.region})` : tl.name;
    },
    // Copies a project's default team leaders onto a job, skipping any already there
    applyProjectTeamLeaders(job, project) {
      if (!project || !project.teamLeaders) return;
      if (!job.teamLeaders) this.$set(job, 'teamLeaders', []);
      if (!job.teamLeaderIds) this.$set(job, 'teamLeaderIds', []);
      project.teamLeaders.forEach((tl) => {
        if (!job.teamLeaderIds.includes(tl.id)) {
          job.teamLeaders.push({ id: tl.id, name: tl.name });
          job.teamLeaderIds.push(tl.id);
        }
      });
    },
    availableTeamLeadersForJob(job) {
      const assignedIds = (job.teamLeaderIds || []);
      return this.teamLeaders.filter(tl => !assignedIds.includes(tl.id));
    },
    addTeamLeaderToJob(job, event) {
      const tlId = event.target.value;
      if (!tlId) return;
      const tl = this.teamLeaders.find(t => t.id === tlId);
      if (!tl) return;
      if (!job.teamLeaders) this.$set(job, 'teamLeaders', []);
      if (!job.teamLeaderIds) this.$set(job, 'teamLeaderIds', []);
      job.teamLeaders.push({ id: tl.id, name: tl.name });
      job.teamLeaderIds.push(tl.id);
      event.target.value = '';
    },
    removeTeamLeaderFromJob(job, tlId) {
      if (!job.teamLeaders || !job.teamLeaderIds) return;
      job.teamLeaders = job.teamLeaders.filter(tl => tl.id !== tlId);
      job.teamLeaderIds = job.teamLeaderIds.filter(id => id !== tlId);
    },
    async assignTesterFromTeam(job, event) {
      const testerId = event.target.value;
      if (!testerId) return;
      const tester = this.teamMembers.find(m => m.id === testerId);
      if (!tester) return;
      this.$set(job, 'contractor', { id: tester.id, name: tester.name });
      // Save immediately for team leader quick-assign
      await setFire({
        collection: "jobs",
        doc: job.id,
        body: { contractor: job.contractor },
      });
      this.showToast("Tester assigned");
    },
    async saveTeamLeaderNotes(job) {
      try {
        await setFire({
          collection: "jobs",
          doc: job.id,
          body: { notes: job.notes || '' },
        });
        this.showToast("Notes saved");
      } catch (e) {
        console.error("Failed to save notes:", e);
        this.showToast("Failed to save notes");
      }
    },
    async saveTeamLeaderStatus(job) {
      try {
        await setFire({
          collection: "jobs",
          doc: job.id,
          body: { status: job.status },
        });
        this.showToast("Status updated");
      } catch (e) {
        console.error("Failed to update status:", e);
        this.showToast("Failed to update status");
      }
    },

    openChangeResolveModal(job) {
      this.changeResolveJob = job;
      this.changeResolveNote = '';
      this.changeResolveModal = true;
    },
    async resolveChangeRequest() {
      if (!this.changeResolveJob) return;
      await setFire({ collection: "jobs", doc: this.changeResolveJob.id, body: { clientSmsStatus: null } });
      this.changeResolveModal = false;
      this.changeResolveJob = null;
      this.changeResolveNote = '';
    },

    // ==================== DATA LOADING ====================

    parseJob(d) {
      const job = { ...d.data(), id: d.id };
      if (job.date && job.date.seconds) {
        job.date = new Date(job.date.seconds * 1000).toISOString();
      } else if (!job.date) {
        job.date = "";
      }
      if (job.rejected) job.testerResponse = "rejected";
      else if (job.accepted) job.testerResponse = "accepted";
      else if (!job.testerResponse) job.testerResponse = "pending";
      return job;
    },

    async loadData() {
      this.loading = true;
      try {
        // Load projects, contractors, customers and team leaders (one-time)
        const promises = [
          getDocs(query(collection(db, "projects"))),
          getContractors(),
          getCustomers(),
          getTeamLeaders(),
        ];
        // If current user is a team leader, also load their team members
        if (this.currentUser && this.currentUser.isTeamLeader && this.currentUser.role !== 'admin') {
          promises.push(getTeamMembers(this.currentUser.id));
        }
        const results = await Promise.all(promises);
        const [projectsSnap, contractorsList, customersList, teamLeadersList] = results;
        const teamMembersList = results[4] || [];

        // Projects
        const projects = [];
        projectsSnap.forEach((d) => {
          projects.push({ ...d.data(), id: d.id });
        });
        projects.sort((a, b) =>
          (a.projectName || "").localeCompare(b.projectName || "")
        );
        this.allProjects = projects;

        // Contractors & Customers
        this.contractors = contractorsList;
        this.allCustomers = customersList || [];
        this.teamLeaders = teamLeadersList || [];
        this.teamMembers = teamMembersList || [];

        // Load available regions for dropdowns
        this.fetchAvailableRegions();

        // Use real-time listener as the sole source for jobs data
        this.startJobsListener();
      } catch (e) {
        console.error("Failed to load data:", e);
        this.showToast("Failed to load data");
      } finally {
        this.loading = false;
      }
    },

    startJobsListener() {
      // Stop existing listener if any
      if (this.jobsUnsubscribe) {
        this.jobsUnsubscribe();
        this.jobsUnsubscribe = null;
      }
      const activeStatuses = ["ON-CALL", "FIRM", "COMMITTED"];
      const q = query(
        collection(db, "jobs"),
        where("status", "in", activeStatuses)
      );
      this.jobsUnsubscribe = onSnapshot(q, (snapshot) => {
        const jobMap = {};
        snapshot.forEach((d) => {
          const job = this.parseJob(d);
          const pid = String(job.projectId);
          if (!jobMap[pid]) jobMap[pid] = [];
          jobMap[pid].push(job);
        });

        // Detect clientSmsStatus changes for alert popup (only for users with receiveSmsAlerts enabled)
        if (this.currentUser && this.currentUser.receiveSmsAlerts) {
          snapshot.docChanges().forEach((change) => {
            if (change.type === "modified") {
              const newData = change.doc.data();
              const status = newData.clientSmsStatus;
              if (status === "CANCELLED" || status === "CHANGED") {
                // Check if this is a new status change (not from initial load)
                const oldJob = this.findJobById(change.doc.id);
                if (oldJob && oldJob.clientSmsStatus !== status) {
                  const job = this.parseJob(change.doc);
                  this.smsAlertJob = job;
                  this.smsAlertStatus = status;
                  this.smsAlertModal = true;
                }
              }
            }
          });
        }

        this.allJobs = jobMap;
      }, (err) => {
        console.error("Jobs listener error:", err);
      });
    },

    findJobById(jobId) {
      for (const jobs of Object.values(this.allJobs)) {
        const found = jobs.find((j) => j.id === jobId);
        if (found) return found;
      }
      return null;
    },

    dismissSmsAlert() {
      this.smsAlertModal = false;
      this.smsAlertJob = null;
      this.smsAlertStatus = "";
    },

    viewAlertJob() {
      const job = this.smsAlertJob;
      if (job) {
        const pid = String(job.projectId);
        const project = this.allProjects.find((p) => String(p.projectId) === pid);
        if (project) this.selectProject(project);
        this.selectedJobId = job.id;
      }
      this.dismissSmsAlert();
    },

    formatDate(d) {
      if (!d) return "";
      return this.$moment(d).format("ddd DD MMM YYYY, HH:mm");
    },

    async loadCompletedJobs(projectId) {
      const pid = String(projectId);
      if (this.completedJobsLoaded[pid]) return;
      this.completedJobsLoading = true;
      try {
        const completedStatuses = ["COMPLETED", "CANCELLED"];
        const snap = await getDocs(query(
          collection(db, "jobs"),
          where("projectId", "==", Number(projectId) || projectId),
          where("status", "in", completedStatuses)
        ));
        const jobs = [];
        snap.forEach((d) => {
          jobs.push(this.parseJob(d));
        });
        // Also try string projectId if no results
        if (jobs.length === 0 && typeof projectId === "number") {
          const snap2 = await getDocs(query(
            collection(db, "jobs"),
            where("projectId", "==", String(projectId)),
            where("status", "in", completedStatuses)
          ));
          snap2.forEach((d) => {
            jobs.push(this.parseJob(d));
          });
        }
        this.$set(this.completedJobsMap, pid, jobs);
        this.$set(this.completedJobsLoaded, pid, true);
      } catch (e) {
        console.error("Failed to load completed jobs:", e);
        this.showToast("Failed to load completed jobs");
      } finally {
        this.completedJobsLoading = false;
      }
    },

    async loadSmsHistory(jobId) {
      this.smsLoading = true;
      try {
        const user = await this.getCurrentUser();
        const token = await user.getIdToken();
        const res = await this.axios.get(
          `${API_BASE}/api/v1/messages/job/${jobId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.smsHistory = res.data.data || res.data || [];
      } catch (e) {
        console.error("Failed to load SMS history:", e);
        this.smsHistory = [];
      } finally {
        this.smsLoading = false;
      }
    },

    async pollTesterLocations() {
      const ids = this.projectTesterIds;
      if (ids.length === 0) return;
      for (const id of ids) {
        try {
          const docSnap = await getDoc(doc(db, "users", id));
          if (docSnap.exists()) {
            const data = docSnap.data();
            if (data.latitude && data.longitude) {
              this.$set(this.testerLocations, id, {
                latitude: data.latitude,
                longitude: data.longitude,
                speed: data.speed || 0,
                lastUpdate: data.lastUpdate || data.updated || null,
                name: data.name || "",
              });
            }
          }
        } catch (e) {
          // Silently ignore
        }
      }
      this.updateMapMarkers();
    },

    startTesterPolling() {
      this.stopTesterPolling();
      this.pollTesterLocations();
      this.testerPollTimer = setInterval(
        () => this.pollTesterLocations(),
        15000
      );
    },

    stopTesterPolling() {
      if (this.testerPollTimer) {
        clearInterval(this.testerPollTimer);
        this.testerPollTimer = null;
      }
    },

    // ==================== JOB CRUD ====================

    toggleCompletedJobs() {
      this.showCompleted = !this.showCompleted;
      if (this.showCompleted && this.selectedProjectId) {
        this.loadCompletedJobs(this.selectedProjectId);
      }
    },

    openAddJobModal() {
      this.newJob = {
        date: "",
        status: "ON-CALL",
        contractor: null,
        region: "",
        mixCode: "",
        meters: "",
        duration: "",
        flexible: false,
        requirements: "",
        pourLocation: "",
        notesForTester: "",
        notes: "",
        jobContact: "",
        jobPhone: "",
        jobEmail: "",
        teamLeaders: [],
        teamLeaderIds: [],
      };
      this.seedCustomFields(this.newJob, this.defaultServiceKey());
      // Reset company/project picker
      this.jobCompanySearch = '';
      this.showJobCompanyDropdown = false;
      this.showJobNewCompany = false;
      this.jobNewCompanyName = '';
      if (this.selectedProject) {
        this.jobSelectedCompany = this.selectedProject.company || '';
        this.jobSelectedProject = this.selectedProject;
        this.jobCompanySearch = this.selectedProject.company || '';
        // The watcher won't fire if the same project was already selected
        this.applyProjectTeamLeaders(this.newJob, this.selectedProject);
      } else {
        this.jobSelectedCompany = '';
        this.jobSelectedProject = null;
      }
      this.showAddJob = true;
    },

    async saveNewJob() {
      const proj = this.jobSelectedProject;
      if (!proj) {
        this.showToast("Please select a company and project");
        return;
      }

      // If creating a new company, do that first
      if (this.showJobNewCompany && this.jobNewCompanyName.trim()) {
        try {
          const customerResult = await postFire({
            collection: 'customers',
            body: { company: this.jobNewCompanyName.trim(), jobs: [] },
          });
          this.allCustomers.push({ id: customerResult.id, company: this.jobNewCompanyName.trim() });
        } catch (e) {
          console.error("Failed to create customer:", e);
        }
      }

      const job = this.newJob;
      this.jobSaving = true;
      try {
        const jobNumber = await getNextJobNumber();
        // Manually-added team leaders, then the project's defaults
        const tls = job.teamLeaders ? [...job.teamLeaders] : [];
        const tlIds = job.teamLeaderIds ? [...job.teamLeaderIds] : [];
        (proj.teamLeaders || []).forEach((tl) => {
          if (!tlIds.includes(tl.id)) {
            tls.push({ id: tl.id, name: tl.name });
            tlIds.push(tl.id);
          }
        });

        const payload = {
          jobNumber,
          client: proj.client || "",
          active: true,
          company: proj.company || "",
          phone: proj.phone || "",
          email: proj.email || "",
          callFrequency: proj.callFrequency || "",
          contact: proj.contact || "",
          secondaryContact: proj.secondaryContact || "",
          secondaryPhone: proj.secondaryPhone || "",
          secondaryEmail: proj.secondaryEmail || "",
          poNumber: proj.poNumber || "",
          region: job.region || proj.region || "",
          projectType: proj.projectType || "",
          customerId: proj.customerId || "",
          projectId: proj.projectId,
          projectName: proj.projectName,
          formattedAddress: proj.formattedAddress || "",
          geohash: proj.geohash || "",
          geopoint: proj.geopoint || null,
          address: proj.address || null,
          status: job.status || "ON-CALL",
          contractor: job.contractor || null,
          meters: job.meters || "",
          duration: job.duration || "",
          flexible: job.flexible || false,
          mixCode: job.mixCode || "",
          requirements: job.requirements || "",
          pourLocation: job.pourLocation || "",
          notesForTester: job.notesForTester || "",
          notes: job.notes || "",
          jobContact: job.jobContact || "",
          jobPhone: job.jobPhone || "",
          jobEmail: job.jobEmail || "",
          accepted: false,
          rejected: false,
          teamLeaders: tls,
          teamLeaderIds: tlIds,
          created: new Date(),
          updated: new Date(),
          ...this.customFieldPayload(job),
        };
        if (job.date) {
          payload.date = getTimeStamp(new Date(job.date));
        }
        const newJobResult = await postFire({ collection: "jobs", body: payload });
        // Create invite notifications for all team leaders on this new job
        for (const tlId of tlIds) {
          try { await createTeamLeaderInvite(tlId, newJobResult.id, payload); } catch (e) { console.error(e); }
        }
        // onSnapshot listener will pick up the new job automatically
        this.showAddJob = false;
        this.showToast("Job created");
      } catch (e) {
        console.error("Failed to create job:", e);
        this.showToast("Failed to create job");
      } finally {
        this.jobSaving = false;
      }
    },

    startEditJob(job) {
      const backup = {
        date: job.date,
        status: job.status,
        contractor: job.contractor,
        mixCode: job.mixCode,
        meters: job.meters,
        duration: job.duration,
        flexible: job.flexible,
        requirements: job.requirements,
        pourLocation: job.pourLocation,
        notesForTester: job.notesForTester,
        notes: job.notes,
        jobContact: job.jobContact || "",
        jobPhone: job.jobPhone || "",
        jobEmail: job.jobEmail || "",
        teamLeaders: job.teamLeaders || [],
        teamLeaderIds: job.teamLeaderIds || [],
      };
      if (this.customFieldsEnabled) {
        // Every key across every service, not just the current one — the admin
        // can switch service mid-edit and Cancel must undo those values too.
        backup.serviceType = job.serviceType || "";
        allFieldKeys(this.customFieldDefs).forEach((key) => {
          backup[key] = job[key] === undefined ? "" : job[key];
        });
      }
      job._backup = JSON.parse(JSON.stringify(backup));
      this.$set(job, "_editing", true);
    },

    cancelEditJob(job) {
      // Restore backup
      if (job._backup) {
        Object.keys(job._backup).forEach((k) => {
          this.$set(job, k, job._backup[k]);
        });
        delete job._backup;
      }
      this.$set(job, "_editing", false);
    },

    async saveJob(job) {
      this.jobSaving = true;
      const proj = this.selectedProject;
      try {
        const tls = job.teamLeaders ? [...job.teamLeaders] : [];
        const tlIds = job.teamLeaderIds ? [...job.teamLeaderIds] : [];

        const payload = {
          client: job.client || proj.client || "",
          active: true,
          company: job.company || proj.company || "",
          phone: job.phone || proj.phone || "",
          email: job.email || proj.email || "",
          callFrequency: job.callFrequency || proj.callFrequency || "",
          contact: job.contact || proj.contact || "",
          secondaryContact:
            job.secondaryContact || proj.secondaryContact || "",
          secondaryPhone: job.secondaryPhone || proj.secondaryPhone || "",
          secondaryEmail: job.secondaryEmail || proj.secondaryEmail || "",
          poNumber: job.poNumber || proj.poNumber || "",
          region: job.region || proj.region || "",
          projectType: job.projectType || proj.projectType || "",
          customerId: job.customerId || proj.customerId || "",
          projectId: proj.projectId,
          projectName: proj.projectName,
          formattedAddress:
            job.formattedAddress || proj.formattedAddress || "",
          geohash: job.geohash || proj.geohash || "",
          geopoint: job.geopoint || proj.geopoint || null,
          // Job fields
          status: job.status || "ON-CALL",
          contractor: job.contractor || null,
          meters: job.meters || "",
          duration: job.duration || "",
          flexible: job.flexible || false,
          mixCode: job.mixCode || "",
          requirements: job.requirements || "",
          pourLocation: job.pourLocation || "",
          notesForTester: job.notesForTester || "",
          notes: job.notes || "",
          jobContact: job.jobContact || "",
          jobPhone: job.jobPhone || "",
          jobEmail: job.jobEmail || "",
          accepted: job.testerResponse === "accepted",
          rejected: job.testerResponse === "rejected",
          teamLeaders: tls,
          teamLeaderIds: tlIds,
          updated: new Date(),
          ...this.customFieldPayload(job),
        };
        if (job.date) {
          payload.date = getTimeStamp(new Date(job.date));
        }
        if (job.address) {
          payload.address = job.address;
        }

        // Determine newly added team leaders (compare to backup)
        const oldTlIds = (job._backup && job._backup.teamLeaderIds) ? job._backup.teamLeaderIds : [];
        const newTlIds = tlIds.filter(id => !oldTlIds.includes(id));

        await putFire({
          collection: "jobs",
          doc: job.id,
          body: payload,
        });

        // Create invite notifications for newly added team leaders
        for (const newId of newTlIds) {
          try { await createTeamLeaderInvite(newId, job.id, payload); } catch (e) { console.error(e); }
        }

        this.$set(job, "_editing", false);
        delete job._backup;

        this.showToast("Job saved");
      } catch (e) {
        console.error("Failed to save job:", e);
        this.showToast("Failed to save job");
      } finally {
        this.jobSaving = false;
      }
    },

    duplicateJob(job) {
      this.newJob = {
        date: job.date || "",
        status: job.status || "ON-CALL",
        contractor: job.contractor || null,
        mixCode: job.mixCode || "",
        meters: job.meters || "",
        duration: job.duration || "",
        flexible: job.flexible || false,
        requirements: job.requirements || "",
        pourLocation: job.pourLocation || "",
        notesForTester: job.notesForTester || "",
        notes: job.notes || "",
        jobContact: job.jobContact || "",
        jobPhone: job.jobPhone || "",
        jobEmail: job.jobEmail || "",
        teamLeaders: job.teamLeaders ? [...job.teamLeaders] : [],
        teamLeaderIds: job.teamLeaderIds ? [...job.teamLeaderIds] : [],
      };
      this.seedCustomFields(this.newJob, resolveService(job), job);
      // Prepopulate company/project picker
      this.jobCompanySearch = '';
      this.showJobCompanyDropdown = false;
      this.showJobNewCompany = false;
      this.jobNewCompanyName = '';
      if (this.selectedProject) {
        this.jobSelectedCompany = this.selectedProject.company || '';
        this.jobSelectedProject = this.selectedProject;
        this.jobCompanySearch = this.selectedProject.company || '';
      } else {
        this.jobSelectedCompany = job.company || '';
        this.jobSelectedProject = job;
        this.jobCompanySearch = job.company || '';
      }
      this.showAddJob = true;
    },

    confirmDeleteJob(job) {
      this.deleteTarget = job;
      this.deleteConfirm = true;
    },

    async deleteJob() {
      const job = this.deleteTarget;
      if (!job) return;
      try {
        if (job.id) {
          await deleteFire({ collection: "jobs", doc: job.id });
        }
        const pid = String(this.selectedProjectId);
        const jobs = this.allJobs[pid] || [];
        const idx = jobs.indexOf(job);
        if (idx >= 0) jobs.splice(idx, 1);
        if (this.selectedJobId === job.id) this.selectedJobId = null;
        this.showToast("Job deleted");
      } catch (e) {
        console.error("Failed to delete job:", e);
        this.showToast("Failed to delete job");
      } finally {
        this.deleteConfirm = false;
        this.deleteTarget = null;
      }
    },

    async updateJobStatus(job) {
      if (!job.id) return;
      try {
        await setFire({
          collection: "jobs",
          doc: job.id,
          body: { status: job.status },
        });
        this.showToast("Status updated");
      } catch (e) {
        console.error("Failed to update status:", e);
      }
    },

    async updateTesterResponse(job) {
      if (!job.id) return;
      const val = job.testerResponse;
      const body = {
        accepted: val === "accepted",
        rejected: val === "rejected",
      };
      if (val === "rejected") body.rejectedDate = new Date();
      try {
        await setFire({ collection: "jobs", doc: job.id, body });
      } catch (e) {
        console.error("Failed to update tester response:", e);
      }
    },

    // ==================== SMS ====================

    async generateJobToken(jobId) {
      const secret = 'kct-client-link-s3cret-2024';
      const encoder = new TextEncoder();
      const key = await crypto.subtle.importKey(
        'raw', encoder.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
      );
      const sig = await crypto.subtle.sign('HMAC', key, encoder.encode(jobId));
      const hex = Array.from(new Uint8Array(sig)).map(b => b.toString(16).padStart(2, '0')).join('');
      return `${jobId}~${hex.slice(0, 16)}`;
    },

    async openSmsModal(job) {
      // Generate client portal token for {link} shortcode
      if (job.id && !job.token) {
        try {
          job.token = await this.generateJobToken(job.id);
        } catch (e) {
          job.token = '';
        }
      }
      this.smsModalJob = job;
      this.smsModalPhone = this.normalizePhone(
        job.jobPhone || job.phone || this.selectedProject?.phone || ""
      );
      const addr =
        job.formattedAddress || this.selectedProject?.formattedAddress || "";
      const dateStr = job.date
        ? this.$moment(job.date).format("DD/MM/YYYY")
        : "";
      this.smsModalMessage = `Hi, this is KCT. Please confirm your job at ${addr} on ${dateStr}. Reply YES to confirm or let us know if anything has changed.`;
      this.smsModal = true;
      this.smsModalHistory = [];
      this.smsModalJobDirty = false;
      this.smsModalJobSaving = false;
      this.smsModalJobSaved = false;
      this.selectedSmsTemplateId = "";
      this.loadSmsTemplates();
      if (job.id) this.loadModalSmsHistory(job.id);
    },

    async saveSmsModalJob() {
      const job = this.smsModalJob;
      if (!job || !job.id) return;
      this.smsModalJobSaving = true;
      try {
        const update = {
          status: job.status || 'ON-CALL',
          meters: job.meters || '',
          updated: new Date(),
        };
        if (job.date) {
          update.date = getTimeStamp(new Date(job.date));
        }
        await putFire({ collection: 'jobs', doc: job.id, body: update });
        this.smsModalJobDirty = false;
        this.smsModalJobSaved = true;
        setTimeout(() => { this.smsModalJobSaved = false; }, 2000);
      } catch (e) {
        console.error('Failed to update job from SMS modal:', e);
        this.showToast('Failed to save job changes');
      } finally {
        this.smsModalJobSaving = false;
      }
    },

    async loadSmsTemplates() {
      try {
        const user = await this.getCurrentUser();
        const token = await user.getIdToken();
        const res = await fetch(
          `${API_BASE}/api/v1/settings/sms/templates`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await res.json();
        this.smsTemplates = (json.data || []).filter(t => t.type === 'single' || t.type === 'all');
      } catch (e) {
        console.error('Failed to load SMS templates:', e);
        this.smsTemplates = [];
      }
    },

    insertSmsShortcode(code) {
      this.smsModalMessage += code;
    },

    buildIndividualSmsBody(template, job) {
      const project = this.selectedProject;
      const link = job.token ? `${window.location.origin}/job/${job.token}` : '';
      const body = template
        .replace(/\{recipientName\}/g, job.jobContact || job.contact || project?.contact || '')
        .replace(/\{receiverName\}/g, job.jobContact || job.contact || project?.contact || '')
        .replace(/\{company\}/g, job.company || project?.company || '')
        .replace(/\{contact\}/g, job.jobContact || job.contact || project?.contact || '')
        .replace(/\{address\}/g, job.formattedAddress || project?.formattedAddress || '')
        .replace(/\{date\}/g, job.date ? this.$moment(job.date).format('DD/MM/YYYY') : '')
        .replace(/\{time\}/g, job.time || '')
        .replace(/\{meters\}/g, job.meters || '')
        .replace(/\{project\}/g, job.projectName || project?.projectName || '')
        .replace(/\{link\}/g, link);
      return applyCustomFieldTokens(this.customFieldDefs, job, body);
    },

    applySmsTemplate() {
      if (!this.selectedSmsTemplateId) return;
      const tpl = this.smsTemplates.find(t => t.id === this.selectedSmsTemplateId);
      if (tpl) {
        this.smsModalMessage = tpl.body;
      }
    },

    async loadModalSmsHistory(jobId) {
      try {
        const user = await this.getCurrentUser();
        const token = await user.getIdToken();
        const res = await this.axios.get(
          `${API_BASE}/api/v1/messages/job/${jobId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.smsModalHistory = res.data.data || res.data || [];
      } catch (e) {
        this.smsModalHistory = [];
      }
    },

    async sendModalSms() {
      if (!this.smsModalPhone || !this.smsModalMessage || !this.smsModalJob)
        return;
      this.smsModalSending = true;
      try {
        const user = await this.getCurrentUser();
        const token = await user.getIdToken();
        const jobId = this.smsModalJob.id;
        if (jobId) {
          await this.axios.post(
            `${API_BASE}/api/v1/messages/job/${jobId}`,
            { message: this.buildIndividualSmsBody(this.smsModalMessage, this.smsModalJob), to: this.normalizePhone(this.smsModalPhone) },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          await this.loadModalSmsHistory(jobId);
          // Also refresh context panel SMS if same job
          if (this.selectedJobId === jobId) {
            this.loadSmsHistory(jobId);
          }
        }
        this.smsModalMessage = "";
        this.showToast("SMS sent");
      } catch (e) {
        console.error("Failed to send SMS:", e);
        this.showToast("Failed to send SMS");
      } finally {
        this.smsModalSending = false;
      }
    },

    async sendContextSms() {
      if (
        !this.smsReplyText.trim() ||
        !this.selectedJob ||
        !this.selectedJob.id
      )
        return;
      this.smsLoading = true;
      try {
        const user = await this.getCurrentUser();
        const token = await user.getIdToken();
        const phone = this.normalizePhone(
          this.selectedJob.jobPhone || this.selectedJob.phone ||
            this.selectedProject?.phone ||
            ""
        );
        await this.axios.post(
          `${API_BASE}/api/v1/messages/job/${this.selectedJob.id}`,
          { message: this.smsReplyText, to: phone },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.smsReplyText = "";
        await this.loadSmsHistory(this.selectedJob.id);
        this.showToast("SMS sent");
      } catch (e) {
        console.error("Failed to send SMS:", e);
        this.showToast("Failed to send SMS");
      } finally {
        this.smsLoading = false;
      }
    },

    // ==================== MAP ====================

    initMap() {
      if (this.contextTab !== "map") return;
      const proj = this.selectedProject;
      if (!proj) return;

      const containerId = "enterprise-map";

      this.$nextTick(() => {
        const container = document.getElementById(containerId);
        if (!container) return;

        // Get center from project coordinates
        const projCoords = getJobCoords(proj);
        const center = projCoords ? [projCoords.lng, projCoords.lat] : [151.2093, -33.8688];
        const zoom = projCoords ? 13 : 10;

        // Destroy old map
        this.destroyMap();

        mapboxgl.accessToken = MAPBOX_TOKEN;
        this.map = new mapboxgl.Map({
          container: containerId,
          style: "mapbox://styles/mapbox/dark-v11",
          center: center,
          zoom: zoom,
        });

        this.map.addControl(
          new mapboxgl.NavigationControl(),
          "top-right"
        );

        // Add job site marker (pulsing indigo marker)
        if (projCoords) {
          const wrapper = document.createElement("div");
          wrapper.style.cssText = "width:44px;height:44px;cursor:pointer;";
          const pulse = document.createElement("div");
          pulse.style.cssText = "position:absolute;top:0;left:0;width:44px;height:44px;border-radius:50%;border:2px solid #6366f1;animation:enterprise-pulse 2s ease-out infinite;pointer-events:none;";
          wrapper.appendChild(pulse);
          const dot = document.createElement("div");
          dot.style.cssText = "position:absolute;top:4px;left:4px;width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:14px;border:3px solid #6366f1;background:#1a1a2e;color:white;box-shadow:0 0 12px #6366f188;";
          dot.innerHTML = '<i class="fa-solid fa-location-dot" style="font-size:14px;color:#6366f1;"></i>';
          wrapper.appendChild(dot);
          const marker = new mapboxgl.Marker({ element: wrapper, anchor: "center" })
            .setLngLat([projCoords.lng, projCoords.lat])
            .setPopup(
              new mapboxgl.Popup({ offset: 10 }).setHTML(
                `<div style="font-size:12px;padding:4px;color:#fff;background:#1a1a2e;"><strong>${proj.projectName || "Job Site"}</strong><br/><span style="color:#999;">${proj.formattedAddress || ""}</span></div>`
              )
            )
            .addTo(this.map);
          this.mapMarkers.push(marker);
        }

        // Start polling tester locations
        this.startTesterPolling();
      });
    },

    updateMapMarkers() {
      if (!this.map) return;

      // Remove old tester markers (keep first marker = job site)
      while (this.mapMarkers.length > 1) {
        const m = this.mapMarkers.pop();
        m.remove();
      }

      const bounds = new mapboxgl.LngLatBounds();
      let hasPoints = false;

      // Add job site to bounds
      const projCoords = getJobCoords(this.selectedProject);
      if (projCoords) {
        bounds.extend([projCoords.lng, projCoords.lat]);
        hasPoints = true;
      }

      // Add tester markers with stage-based colors (only for filtered/visible jobs)
      const visibleJobs = this.filteredCurrentJobs;
      const visibleTesterIds = new Set();
      visibleJobs.forEach((j) => {
        if (j.contractor && j.contractor.id) visibleTesterIds.add(j.contractor.id);
      });

      visibleTesterIds.forEach((id) => {
        const loc = this.testerLocations[id];
        if (!loc || !loc.latitude || !loc.longitude) return;

        // Find the job this tester is assigned to, for stage detection
        const testerJob = visibleJobs.find(
          (j) => j.contractor && j.contractor.id === id
        );
        const stage = testerJob ? this.getJobStageForMap(testerJob) : "idle";
        const color = this.mapStageColor(stage);

        // Highlight if selected
        const isSelected =
          this.selectedJob &&
          this.selectedJob.contractor &&
          this.selectedJob.contractor.id === id;
        const size = isSelected ? 52 : 44;
        const innerSize = isSelected ? 42 : 36;
        const innerOffset = isSelected ? 5 : 4;

        const wrapper = document.createElement("div");
        wrapper.style.cssText = `width:${size}px;height:${size}px;cursor:pointer;`;
        const pulse = document.createElement("div");
        pulse.style.cssText = `position:absolute;top:0;left:0;width:${size}px;height:${size}px;border-radius:50%;border:2px solid ${color};animation:enterprise-pulse 2s ease-out infinite;pointer-events:none;`;
        wrapper.appendChild(pulse);
        const dot = document.createElement("div");
        dot.style.cssText = `position:absolute;top:${innerOffset}px;left:${innerOffset}px;width:${innerSize}px;height:${innerSize}px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:${isSelected ? 16 : 12}px;border:3px solid ${color};background:#1a1a2e;color:white;box-shadow:0 0 12px ${color}88;`;
        // Show initials inside the marker
        const name = loc.name || (testerJob && testerJob.contractor ? testerJob.contractor.name : "?");
        const ini = name ? name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) : "?";
        dot.innerHTML = `<span style="font-size:${isSelected ? 14 : 11}px;font-weight:bold;color:${color};">${ini}</span>`;
        wrapper.appendChild(dot);

        const popupContent = `<div style="font-size:12px;padding:6px;color:#fff;background:#1a1a2e;min-width:120px;">
          <strong>${loc.name || "Tester"}</strong>
          <div style="color:#999;margin-top:2px;">${this.mapStageLabel(stage)}</div>
          ${testerJob ? `<div style="color:#6366f1;margin-top:2px;">${this.formatJobTime(testerJob)} — ${testerJob.status}</div>` : ""}
        </div>`;

        const marker = new mapboxgl.Marker({ element: wrapper, anchor: "center" })
          .setLngLat([loc.longitude, loc.latitude])
          .setPopup(new mapboxgl.Popup({ offset: 10 }).setHTML(popupContent))
          .addTo(this.map);
        this.mapMarkers.push(marker);
        bounds.extend([loc.longitude, loc.latitude]);
        hasPoints = true;
      });

      if (hasPoints && this.mapMarkers.length > 1) {
        this.map.fitBounds(bounds, { padding: { top: 80, bottom: 80, left: 340, right: 60 }, maxZoom: 15 });
      }
    },

    destroyMap() {
      this.mapMarkers.forEach((m) => m.remove());
      this.mapMarkers = [];
      if (this.map) {
        this.map.remove();
        this.map = null;
      }
    },

    // ==================== NAVIGATION ====================

    async deepLinkToJob(jobId) {
      // Wait for the onSnapshot listener to populate allJobs if it hasn't yet
      let foundJob = null;
      let foundPid = null;
      for (let attempt = 0; attempt < 20; attempt++) {
        for (const pid of Object.keys(this.allJobs)) {
          const job = this.allJobs[pid].find((j) => j.id === jobId);
          if (job) {
            foundJob = job;
            foundPid = pid;
            break;
          }
        }
        if (foundJob) break;
        await new Promise((r) => setTimeout(r, 250));
      }

      // If not found in active jobs (e.g. completed/cancelled), fetch directly
      if (!foundJob) {
        try {
          const docSnap = await getDoc(doc(db, "jobs", jobId));
          if (docSnap.exists()) {
            foundJob = this.parseJob(docSnap);
            foundPid = String(foundJob.projectId);
          }
        } catch (e) {
          console.error("Failed to fetch deep-linked job:", e);
        }
      }

      if (!foundJob) return;

      // Find and select the project
      const project = this.allProjects.find(
        (p) => String(p.projectId) === foundPid
      );
      if (project) {
        // Switch to correct tab (current vs archived)
        if (project.closed === true || project.active === false) {
          this.projectTab = "archived";
        } else {
          this.projectTab = "current";
        }
        this.selectProject(project);
      } else {
        // Project not found in allProjects, just set the ID directly
        this.selectedProjectId = foundPid;
      }

      // Select the specific job
      this.$nextTick(() => {
        this.selectedJobId = foundJob.id;
      });
    },

    deepLinkToProject(projectId) {
      const project = this.allProjects.find(
        (p) => String(p.projectId) === String(projectId) || p.id === String(projectId)
      );
      if (!project) return;

      if (project.closed === true || project.active === false) {
        this.projectTab = "archived";
      } else {
        this.projectTab = "current";
      }
      this.selectProject(project);

      // If editProject query param is set, open the edit modal
      if (this.$route.query.editProject === 'true') {
        this.$nextTick(() => {
          this.openEditProject();
        });
      }

      // Clean up query params
      const query = { ...this.$route.query };
      delete query.projectId;
      delete query.editProject;
      this.$router.replace({ query }).catch(() => {});
    },

    selectProject(project) {
      this.selectedProjectId = project.projectId;
      this.startTesterPolling();
      this.loadTestReports();
    },

    loadTestReports() {
      if (this.unsubTestReports) {
        this.unsubTestReports();
        this.unsubTestReports = null;
      }
      const q = query(
        collection(db, 'incomingFiles'),
        where('status', '==', 'allocated')
      );
      this.unsubTestReports = onSnapshot(q, (snap) => {
        const counts = {};
        const files = {};
        snap.docs.forEach(d => {
          const data = d.data();
          const jobId = data.jobId;
          if (jobId) {
            counts[jobId] = (counts[jobId] || 0) + 1;
            if (!files[jobId]) files[jobId] = [];
            files[jobId].push({
              id: d.id,
              subject: data.subject || 'Test Report',
              attachments: data.attachments || [],
            });
          }
        });
        this.jobTestReports = counts;
        this.testReportFiles = files;
      });
    },

    getTestReportCount(jobId) {
      return this.jobTestReports[jobId] || 0;
    },

    getTestReportFiles(jobId) {
      return this.testReportFiles[jobId] || [];
    },

    selectJob(job) {
      if (job._editing) return;
      this.selectedJobId = job.id || null;
    },

    goBackToDashboard() {
      this.selectedProjectId = null;
      this.selectedJobId = null;
    },

    switchToLegacy() {
      localStorage.setItem("jobsViewMode", "legacy");
      this.$router.push({ name: "JobsLegacy" });
    },

    openEditProject() {
      if (!this.selectedProject) return;
      const p = this.selectedProject;
      this.editProject = {
        projectName: p.projectName || "",
        company: p.company || "",
        customerId: p.customerId || "",
        contact: p.contact || "",
        phone: p.phone || "",
        email: p.email || "",
        formattedAddress: p.formattedAddress || "",
        region: p.region || "",
        poNumber: p.poNumber || "",
        secondaryContact: p.secondaryContact || "",
        secondaryPhone: p.secondaryPhone || "",
        secondaryEmail: p.secondaryEmail || "",
        callFrequency: p.callFrequency || "",
        notes: p.notes || "",
        teamLeaders: p.teamLeaders ? [...p.teamLeaders] : [],
        teamLeaderIds: p.teamLeaderIds ? [...p.teamLeaderIds] : [],
      };
      this.editCompanySearch = p.company || "";
      this.showEditCompanyDropdown = false;
      this.showEditNewCompany = false;
      this.editNewCompanyName = '';
      this.addressResults = [];
      this.showEditProject = true;
    },

    async saveEditProject() {
      if (!this.selectedProject) return;
      this.editProjectSaving = true;
      try {
        // If creating a new company, do that first
        if (this.showEditNewCompany && this.editNewCompanyName.trim()) {
          const customerResult = await postFire({
            collection: 'customers',
            body: { company: this.editNewCompanyName.trim(), jobs: [] },
          });
          this.editProject.company = this.editNewCompanyName.trim();
          this.editProject.customerId = customerResult.id;
          this.allCustomers.push({ id: customerResult.id, company: this.editNewCompanyName.trim() });
        }

        const docId = this.selectedProject.id || String(this.selectedProject.projectId);
        const editBody = { ...this.editProject };
        // Map geocode results to proper field names
        if (editBody._lat && editBody._lng) {
          editBody.latitude = editBody._lat;
          editBody.longitude = editBody._lng;
        }
        delete editBody._lat;
        delete editBody._lng;
        await putFire({
          collection: "projects",
          doc: docId,
          body: editBody,
        });

        // Sync project-level fields to all jobs for this project
        const projectId = this.selectedProject.projectId;
        const jobFields = {
          company: this.editProject.company || "",
          customerId: this.editProject.customerId || "",
          phone: this.editProject.phone || "",
          email: this.editProject.email || "",
          contact: this.editProject.contact || "",
          secondaryContact: this.editProject.secondaryContact || "",
          secondaryPhone: this.editProject.secondaryPhone || "",
          secondaryEmail: this.editProject.secondaryEmail || "",
          poNumber: this.editProject.poNumber || "",
          region: this.editProject.region || "",
          formattedAddress: this.editProject.formattedAddress || "",
          projectName: this.editProject.projectName || "",
        };
        const jobsSnap = await getDocs(
          query(collection(db, "jobs"), where("projectId", "==", projectId))
        );
        if (!jobsSnap.empty) {
          const batch = writeBatch(db);
          jobsSnap.docs.forEach((d) => {
            batch.update(doc(db, "jobs", d.id), jobFields);
          });
          await batch.commit();
        }

        // Update local data
        const idx = this.allProjects.findIndex(
          (p) => p.projectId == this.selectedProjectId
        );
        if (idx >= 0) {
          Object.assign(this.allProjects[idx], this.editProject);
          // These keys may not exist on older project docs, so Object.assign
          // alone wouldn't make them reactive
          this.$set(this.allProjects[idx], "teamLeaders", this.editProject.teamLeaders || []);
          this.$set(this.allProjects[idx], "teamLeaderIds", this.editProject.teamLeaderIds || []);
        }
        this.showEditProject = false;
        this.showToast("Project updated");
      } catch (e) {
        console.error("Failed to update project:", e);
        this.showToast("Failed to update project");
      } finally {
        this.editProjectSaving = false;
      }
    },

    openLegacyEdit() {
      // Find a job from this project to open in legacy view
      const jobs = this.allJobs[this.selectedProjectId] || [];
      if (jobs.length > 0 && jobs[0].id) {
        this.$router.push({
          name: "JobsLegacy",
          query: { jobId: jobs[0].id },
        });
      } else {
        this.$router.push({ name: "JobsLegacy" });
      }
    },

    // ==================== CLIENT LINK ====================

    async openLinkModal(job) {
      this.linkJob = job;
      this.linkCopied = false;
      const secret = "kct-client-link-s3cret-2024";
      const encoder = new TextEncoder();
      const key = await crypto.subtle.importKey(
        "raw",
        encoder.encode(secret),
        { name: "HMAC", hash: "SHA-256" },
        false,
        ["sign"]
      );
      const sig = await crypto.subtle.sign(
        "HMAC",
        key,
        encoder.encode(job.id)
      );
      const hex = Array.from(new Uint8Array(sig))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      const token = `${job.id}~${hex.slice(0, 16)}`;
      this.linkUrl = `${window.location.origin}/job/${token}`;
      this.linkModal = true;
    },

    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.linkUrl);
        this.linkCopied = true;
        setTimeout(() => {
          this.linkCopied = false;
        }, 2000);
      } catch (e) {
        const input = document.createElement("input");
        input.value = this.linkUrl;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
        this.linkCopied = true;
        setTimeout(() => {
          this.linkCopied = false;
        }, 2000);
      }
    },

    // ==================== PROJECT NOTES ====================

    startEditProjectNotes() {
      this.projectNotesText = this.selectedProject?.notes || "";
      this.editingProjectNotes = true;
    },

    async saveProjectNotes() {
      if (!this.selectedProject) return;
      try {
        await setFire({
          collection: "projects",
          doc: String(this.selectedProject.projectId),
          body: { notes: this.projectNotesText },
        });
        this.$set(this.selectedProject, "notes", this.projectNotesText);
        this.editingProjectNotes = false;
        this.showToast("Notes saved");
      } catch (e) {
        console.error("Failed to save notes:", e);
        this.showToast("Failed to save notes");
      }
    },

    // ==================== UTILITY ====================

    getCurrentUser() {
      return new Promise((resolve, reject) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          unsubscribe();
          user
            ? resolve(user)
            : reject(new Error("No user is signed in."));
        });
      });
    },

    normalizePhone(phone) {
      if (!phone) return "";
      let p = phone.replace(/[\s\-\(\)]/g, "");
      if (p.startsWith("04")) p = "+61" + p.slice(1);
      else if (p.startsWith("614") && !p.startsWith("+")) p = "+" + p;
      else if (p.startsWith("61") && !p.startsWith("+")) p = "+" + p;
      return p;
    },

    formatJobDate(job) {
      if (!job.date) return "No date";
      return this.$moment(job.date).format("ddd DD MMM YYYY, HH:mm");
    },

    formatTimestamp(ts) {
      if (!ts) return "";
      if (ts.seconds)
        return this.$moment(ts.seconds * 1000).format("DD MMM YYYY");
      return this.$moment(ts).format("DD MMM YYYY");
    },

    formatSmsTimestamp(ts) {
      if (!ts) return "";
      if (ts.seconds)
        return this.$moment(ts.seconds * 1000).format("DD/MM/YYYY HH:mm");
      if (ts._seconds)
        return this.$moment(ts._seconds * 1000).format(
          "DD/MM/YYYY HH:mm"
        );
      return this.$moment(ts).format("DD/MM/YYYY HH:mm");
    },

    formatTesterLastUpdate(ts) {
      if (!ts) return "";
      if (ts.seconds)
        return this.$moment(ts.seconds * 1000).fromNow();
      return this.$moment(ts).fromNow();
    },

    toDate(d) {
      if (!d) return 0;
      if (d.seconds) return d.seconds * 1000;
      return new Date(d).getTime() || 0;
    },

    getTimestampMs(ts) {
      if (!ts) return 0;
      if (ts.seconds) return ts.seconds * 1000;
      if (ts._seconds) return ts._seconds * 1000;
      return new Date(ts).getTime() || 0;
    },

    isJobToday(job) {
      if (!job.date) return false;
      return this.$moment(job.date).isSame(this.$moment(), "day");
    },

    isJobTomorrow(job) {
      if (!job.date) return false;
      return this.$moment(job.date).isSame(
        this.$moment().add(1, "day"),
        "day"
      );
    },

    getJobCount(project) {
      const jobs = this.allJobs[String(project.projectId)] || [];
      return jobs.length;
    },

    statusBadgeClass(status) {
      const map = {
        "ON-CALL": "bg-yellow-100 text-yellow-800",
        FIRM: "bg-green-100 text-green-800",
        COMMITTED: "bg-blue-100 text-blue-800",
        COMPLETED: "bg-green-600 text-white",
        CANCELLED: "bg-red-100 text-red-800",
      };
      return map[status] || "bg-gray-100 text-gray-600";
    },

    statusSelectClass(status) {
      const map = {
        "ON-CALL": "border-yellow-300 bg-yellow-50 text-yellow-700",
        FIRM: "border-green-300 bg-green-50 text-green-700",
        COMMITTED: "border-blue-300 bg-blue-50 text-blue-700",
        COMPLETED: "border-green-500 bg-green-100 text-green-800",
        CANCELLED: "border-red-300 bg-red-50 text-red-700",
      };
      return map[status] || "border-gray-200 bg-gray-50 text-gray-600";
    },

    testerResponseClass(response) {
      const map = {
        accepted: "bg-green-100 text-green-700",
        rejected: "bg-red-100 text-red-700",
        pending: "bg-gray-100 text-gray-600",
      };
      return map[response] || "bg-gray-100 text-gray-600";
    },

    testerResponseSelectClass(response) {
      const map = {
        accepted: "border-green-300 bg-green-50 text-green-700",
        rejected: "border-red-300 bg-red-50 text-red-700",
        pending: "border-gray-200 bg-gray-50 text-gray-600",
      };
      return map[response] || "border-gray-200 bg-gray-50 text-gray-600";
    },

    // ==================== MAP HELPERS ====================

    formatJobTime(job) {
      if (!job.date) return "--:--";
      return this.$moment(job.date).format("HH:mm");
    },

    formatJobDateShort(job) {
      if (!job.date) return "No date";
      return this.$moment(job.date).format("ddd DD MMM");
    },

    initials(name) {
      if (!name) return "?";
      return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
    },

    getJobStageForMap(job) {
      if (!job.contractor || !job.contractor.id) return "idle";
      // Manual en-route takes priority over vehicle online status
      if (job.enRoute) return "en_route";
      if (job.status === "COMPLETED") return "completed";
      const loc = this.testerLocations[job.contractor.id];

      // Check proximity using last known position before online check
      const jobCoords = getJobCoords(this.selectedProject);
      if (jobCoords && loc && loc.latitude && loc.longitude) {
        const dist = haversineDistance(loc.latitude, loc.longitude, jobCoords.lat, jobCoords.lng);
        if (dist < 200) return "testing";
        if (dist < 1000) return "arrived";
      }

      if (!loc) return "offline";
      if (!jobCoords) return "idle";
      const speed = loc.speed || 0;
      return speed > 5 ? "moving" : "idle";
    },

    mapStageLabel(stage) {
      const labels = {
        en_route: "En Route", arrived: "Arrived",
        testing: "Testing", completed: "Completed",
        idle: "Idle", offline: "Offline",
      };
      return labels[stage] || stage;
    },

    mapStagePillClass(stage) {
      const map = {
        en_route: "bg-blue-900 text-blue-300",
        arrived: "bg-yellow-900 text-yellow-300",
        testing: "bg-purple-900 text-purple-300",
        completed: "bg-green-900 text-green-300",
      };
      return map[stage] || "bg-gray-700 text-gray-300";
    },

    mapStageColor(stage) {
      const map = {
        en_route: "#3b82f6", arrived: "#eab308",
        testing: "#a855f7", completed: "#22c55e",
        idle: "#9ca3af", offline: "#6b7280",
      };
      return map[stage] || "#6b7280";
    },

    mapStatusBadgeClass(status) {
      const map = {
        "ON-CALL": "bg-yellow-900 text-yellow-300",
        FIRM: "bg-green-900 text-green-300",
        COMMITTED: "bg-blue-900 text-blue-300",
        COMPLETED: "bg-green-700 text-green-100",
        CANCELLED: "bg-red-900 text-red-300",
      };
      return map[status] || "bg-gray-700 text-gray-300";
    },

    milestones(stage) {
      const stageOrder = ["en_route", "arrived", "testing", "completed"];
      const idx = stageOrder.indexOf(stage);
      return [
        { label: "En Route", color: "bg-blue-500", active: idx >= 0 },
        { label: "Arrived", color: "bg-yellow-500", active: idx >= 1 },
        { label: "Testing", color: "bg-purple-500", active: idx >= 2 },
        { label: "Completed", color: "bg-green-500", active: idx >= 3 },
      ];
    },

    centerMapOnJob(job) {
      if (!this.map) return;
      const coords = getJobCoords(this.selectedProject);
      if (!coords) return;
      this.map.flyTo({ center: [coords.lng, coords.lat], zoom: 14, duration: 800 });
    },

    // ==================== CLOSE / REOPEN PROJECT ====================

    promptCloseProject() {
      this.closeProjectTarget = this.selectedProject;
    },

    async confirmCloseProject() {
      if (!this.closeProjectTarget) return;
      this.closingProject = true;
      try {
        const docId = this.closeProjectTarget.id || String(this.closeProjectTarget.projectId);
        await putFire({
          collection: "projects",
          doc: docId,
          body: { closed: true },
        });
        this.$set(this.closeProjectTarget, "closed", true);
        this.closeProjectTarget = null;
        this.showToast("Project closed");
      } catch (e) {
        console.error("Failed to close project:", e);
        this.showToast("Failed to close project");
      } finally {
        this.closingProject = false;
      }
    },

    promptReopenProject() {
      this.reopenProjectTarget = this.selectedProject;
    },

    async confirmReopenProject() {
      if (!this.reopenProjectTarget) return;
      this.reopeningProject = true;
      try {
        const docId = this.reopenProjectTarget.id || String(this.reopenProjectTarget.projectId);
        await putFire({
          collection: "projects",
          doc: docId,
          body: { closed: false },
        });
        this.$set(this.reopenProjectTarget, "closed", false);
        this.reopenProjectTarget = null;
        this.showToast("Project reopened");
      } catch (e) {
        console.error("Failed to reopen project:", e);
        this.showToast("Failed to reopen project");
      } finally {
        this.reopeningProject = false;
      }
    },

    // ==================== AUTO ARCHIVE ====================

    openAutoArchiveModal() {
      this.autoArchiveModal = true;
      this.autoArchiveTab = 'archive';
      this.autoArchiveRunning = false;
      this.autoArchiveDone = false;
      this.autoArchiveCount = 0;
      this.autoArchiveProgress = 0;
      this.autoArchiveTotal = 0;
      this.autoArchiveDays = 30;
      this.autoArchiveLog = [];
    },

    autoArchiveAddLog(msg, type = 'info') {
      this.autoArchiveLog.push({ msg, type });
      this.$nextTick(() => {
        const el = this.$refs.autoArchiveLogRef;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    async runAutoArchive() {
      if (!this.autoArchiveDays || this.autoArchiveDays < 1) return;
      this.autoArchiveRunning = true;
      this.autoArchiveCount = 0;
      this.autoArchiveProgress = 0;
      this.autoArchiveLog = [];

      try {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - this.autoArchiveDays);
        const cutoffMs = cutoff.getTime();

        const openProjects = this.allProjects.filter(p => p.closed !== true);
        this.autoArchiveTotal = openProjects.length;
        this.autoArchiveAddLog(`Found ${openProjects.length} open projects to check`);

        const toArchive = [];

        for (const project of openProjects) {
          this.autoArchiveProgress++;
          const rawPid = project.projectId || project.id;
          const pid = typeof rawPid === "string" ? parseInt(rawPid, 10) : rawPid;

          const jobsQuery = query(
            collection(db, "jobs"),
            where("projectId", "==", pid)
          );
          const jobsSnap = await getDocs(jobsQuery);

          if (jobsSnap.empty) {
            this.autoArchiveAddLog(`#${pid} ${project.projectName || '(unnamed)'} — no jobs, skipped`);
            continue;
          }

          let newestMs = 0;
          jobsSnap.forEach(d => {
            const data = d.data();
            let ms = 0;
            if (data.date && data.date.seconds) {
              ms = data.date.seconds * 1000;
            } else if (data.date) {
              ms = new Date(data.date).getTime() || 0;
            }
            if (ms > newestMs) newestMs = ms;
          });

          if (newestMs > 0 && newestMs < cutoffMs) {
            toArchive.push(project);
            const daysAgo = Math.round((Date.now() - newestMs) / 86400000);
            this.autoArchiveAddLog(`#${pid} ${project.projectName || '(unnamed)'} — last job ${daysAgo}d ago, archiving`, 'match');
          }
        }

        if (toArchive.length > 0) {
          this.autoArchiveAddLog(`Archiving ${toArchive.length} projects...`);
          const batchSize = 500;
          for (let i = 0; i < toArchive.length; i += batchSize) {
            const batch = writeBatch(db);
            const chunk = toArchive.slice(i, i + batchSize);
            for (const p of chunk) {
              const docId = p.id || String(p.projectId);
              batch.update(doc(db, "projects", docId), { closed: true });
            }
            await batch.commit();
            for (const p of chunk) {
              this.$set(p, "closed", true);
            }
          }
        }

        this.autoArchiveCount = toArchive.length;
        this.autoArchiveDone = true;
        this.autoArchiveAddLog(`Done — ${toArchive.length} project${toArchive.length === 1 ? '' : 's'} archived`, 'done');
      } catch (e) {
        console.error("Auto archive failed:", e);
        this.autoArchiveAddLog(`Error: ${e.message}`, 'match');
        this.showToast("Auto archive failed");
      } finally {
        this.autoArchiveRunning = false;
      }
    },

    async runAutoReopen() {
      if (!this.autoArchiveDays || this.autoArchiveDays < 1) return;
      this.autoArchiveRunning = true;
      this.autoArchiveCount = 0;
      this.autoArchiveProgress = 0;
      this.autoArchiveLog = [];

      try {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - this.autoArchiveDays);
        const cutoffMs = cutoff.getTime();

        const closedProjects = this.allProjects.filter(p => p.closed === true);
        this.autoArchiveTotal = closedProjects.length;
        this.autoArchiveAddLog(`Found ${closedProjects.length} closed projects to check`);

        const toReopen = [];

        for (const project of closedProjects) {
          this.autoArchiveProgress++;
          const rawPid = project.projectId || project.id;
          const pid = typeof rawPid === "string" ? parseInt(rawPid, 10) : rawPid;

          const jobsQuery = query(
            collection(db, "jobs"),
            where("projectId", "==", pid)
          );
          const jobsSnap = await getDocs(jobsQuery);

          if (jobsSnap.empty) continue;

          let newestMs = 0;
          jobsSnap.forEach(d => {
            const data = d.data();
            let ms = 0;
            if (data.date && data.date.seconds) {
              ms = data.date.seconds * 1000;
            } else if (data.date) {
              ms = new Date(data.date).getTime() || 0;
            }
            if (ms > newestMs) newestMs = ms;
          });

          if (newestMs >= cutoffMs) {
            toReopen.push(project);
            const daysAgo = Math.round((Date.now() - newestMs) / 86400000);
            this.autoArchiveAddLog(`#${pid} ${project.projectName || '(unnamed)'} — job ${daysAgo}d ago, reopening`, 'match');
          }
        }

        if (toReopen.length > 0) {
          this.autoArchiveAddLog(`Reopening ${toReopen.length} projects...`);
          const batchSize = 500;
          for (let i = 0; i < toReopen.length; i += batchSize) {
            const batch = writeBatch(db);
            const chunk = toReopen.slice(i, i + batchSize);
            for (const p of chunk) {
              const docId = p.id || String(p.projectId);
              batch.update(doc(db, "projects", docId), { closed: false });
            }
            await batch.commit();
            for (const p of chunk) {
              this.$set(p, "closed", false);
            }
          }
        }

        this.autoArchiveCount = toReopen.length;
        this.autoArchiveDone = true;
        this.autoArchiveAddLog(`Done — ${toReopen.length} project${toReopen.length === 1 ? '' : 's'} reopened`, 'done');
      } catch (e) {
        console.error("Auto reopen failed:", e);
        this.autoArchiveAddLog(`Error: ${e.message}`, 'match');
        this.showToast("Auto reopen failed");
      } finally {
        this.autoArchiveRunning = false;
      }
    },

    async runCloseEmpty() {
      this.autoArchiveRunning = true;
      this.autoArchiveCount = 0;
      this.autoArchiveProgress = 0;
      this.autoArchiveLog = [];

      try {
        const openProjects = this.allProjects.filter(p => p.closed !== true);
        this.autoArchiveTotal = openProjects.length;
        this.autoArchiveAddLog(`Found ${openProjects.length} open projects to check`);

        const toClose = [];

        for (const project of openProjects) {
          this.autoArchiveProgress++;
          const pid = project.projectId || project.id;
          const activeJobs = this.allJobs[String(pid)] || [];

          if (activeJobs.length === 0) {
            toClose.push(project);
            this.autoArchiveAddLog(`#${pid} ${project.projectName || '(unnamed)'} — 0 active jobs, closing`, 'match');
          }
        }

        if (toClose.length > 0) {
          this.autoArchiveAddLog(`Closing ${toClose.length} empty projects...`);
          const batchSize = 500;
          for (let i = 0; i < toClose.length; i += batchSize) {
            const batch = writeBatch(db);
            const chunk = toClose.slice(i, i + batchSize);
            for (const p of chunk) {
              const docId = p.id || String(p.projectId);
              batch.update(doc(db, "projects", docId), { closed: true });
            }
            await batch.commit();
            for (const p of chunk) {
              this.$set(p, "closed", true);
            }
          }
        }

        this.autoArchiveCount = toClose.length;
        this.autoArchiveDone = true;
        this.autoArchiveAddLog(`Done — ${toClose.length} empty project${toClose.length === 1 ? '' : 's'} closed`, 'done');
      } catch (e) {
        console.error("Close empty failed:", e);
        this.autoArchiveAddLog(`Error: ${e.message}`, 'match');
        this.showToast("Close empty projects failed");
      } finally {
        this.autoArchiveRunning = false;
      }
    },

    // ==================== TESTER HELPERS ====================

    getTesterPhone(contractor) {
      if (!contractor) return null;
      if (contractor.phone) return contractor.phone;
      // Look up from loaded contractors list
      const c = this.contractors.find(
        (tc) => tc.id === (contractor.id || contractor)
      );
      return c ? c.phone : null;
    },

    // ==================== ADDRESS GEOCODING ====================

    debounceAddressGeocode() {
      if (this.addressGeoTimer) clearTimeout(this.addressGeoTimer);
      this.addressGeoTimer = setTimeout(() => {
        this.geocodeNewProjectAddress();
      }, 300);
    },

    async geocodeAddress(query) {
      if (!query || query.trim().length < 3) {
        this.addressResults = [];
        return;
      }
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query.trim())}.json?access_token=${MAPBOX_TOKEN}&country=au&limit=5`;
        const res = await fetch(url);
        const data = await res.json();
        this.addressResults = data.features || [];
      } catch (e) {
        console.error('Geocode error:', e);
        this.addressResults = [];
      }
    },

    async geocodeNewProjectAddress() {
      await this.geocodeAddress(this.newProject.formattedAddress);
    },

    debounceEditAddressGeocode() {
      if (this.addressGeoTimer) clearTimeout(this.addressGeoTimer);
      this.addressGeoTimer = setTimeout(() => {
        this.geocodeAddress(this.editProject.formattedAddress);
      }, 300);
    },

    async fetchAvailableRegions() {
      try {
        const snap = await getDocs(query(collection(db, "regions"), orderBy("name")));
        const list = [];
        snap.forEach((d) => { list.push({ ...d.data(), id: d.id }); });
        this.availableRegions = list;
      } catch (e) {
        console.error("Failed to load regions:", e);
      }
    },

    selectAddress(result) {
      this.newProject.formattedAddress = result.place_name;
      if (result.center) {
        this.newProject._lng = result.center[0];
        this.newProject._lat = result.center[1];
        const match = detectRegion(result.center[1], result.center[0], this.availableRegions);
        if (match) {
          this.newProject.region = match.name;
        }
      }
      this.addressResults = [];
    },

    selectEditAddress(result) {
      this.editProject.formattedAddress = result.place_name;
      if (result.center) {
        this.editProject._lng = result.center[0];
        this.editProject._lat = result.center[1];
        const match = detectRegion(result.center[1], result.center[0], this.availableRegions);
        if (match) {
          this.editProject.region = match.name;
        }
      }
      this.addressResults = [];
    },

    // ==================== ADD PROJECT ====================

    openAddProject() {
      this.newProject = {
        projectName: "",
        company: "",
        customerId: "",
        contact: "",
        phone: "",
        email: "",
        formattedAddress: "",
        region: "",
        poNumber: "",
        secondaryContact: "",
        secondaryPhone: "",
        secondaryEmail: "",
        callFrequency: "",
        teamLeaders: [],
        teamLeaderIds: [],
      };
      this.customerSearch = '';
      this.showCustomerDropdown = false;
      this.showNewCustomerInput = false;
      this.newCustomerName = '';
      this.addressResults = [];
      this.showAddProject = true;
    },
    selectCustomer(customer) {
      this.newProject.customerId = customer.id;
      this.newProject.company = customer.company;
      this.customerSearch = customer.company;
      this.showCustomerDropdown = false;
    },

    async saveNewProject() {
      // Determine company name and customerId
      let companyName = this.newProject.company;
      let customerId = this.newProject.customerId;

      if (this.showNewCustomerInput) {
        companyName = this.newCustomerName.trim();
        customerId = '';
      }

      if (!this.newProject.projectName && !companyName) {
        this.showToast("Project name or company is required");
        return;
      }
      this.newProjectSaving = true;
      try {
        // If creating a new customer, do that first
        if (this.showNewCustomerInput && companyName) {
          const customerResult = await postFire({
            collection: 'customers',
            body: { company: companyName, jobs: [] },
          });
          customerId = customerResult.id;
          this.allCustomers.push({ id: customerId, company: companyName });
        }

        // Generate a projectId (max existing + 1)
        let maxId = 0;
        this.allProjects.forEach((p) => {
          const id = parseInt(p.projectId, 10);
          if (!isNaN(id) && id > maxId) maxId = id;
        });
        const newId = maxId + 1;

        const payload = {
          projectId: newId,
          projectName: this.newProject.projectName,
          company: companyName,
          customerId: customerId || '',
          contact: this.newProject.contact,
          phone: this.newProject.phone,
          email: this.newProject.email,
          formattedAddress: this.newProject.formattedAddress,
          region: this.newProject.region,
          poNumber: this.newProject.poNumber,
          secondaryContact: this.newProject.secondaryContact,
          secondaryPhone: this.newProject.secondaryPhone,
          secondaryEmail: this.newProject.secondaryEmail,
          callFrequency: this.newProject.callFrequency,
          teamLeaders: this.newProject.teamLeaders || [],
          teamLeaderIds: this.newProject.teamLeaderIds || [],
          active: true,
          closed: false,
          created: new Date(),
          ...(this.newProject._lat && this.newProject._lng ? {
            latitude: this.newProject._lat,
            longitude: this.newProject._lng,
          } : {}),
        };

        await setFire({
          collection: "projects",
          doc: String(newId),
          body: payload,
        });

        // Create a blank initial job so the project is findable via the jobs listener
        const jobNumber = await getNextJobNumber();
        await postFire({
          collection: "jobs",
          body: {
            jobNumber,
            active: true,
            company: companyName,
            customerId: customerId || '',
            projectId: newId,
            projectName: this.newProject.projectName,
            formattedAddress: this.newProject.formattedAddress || '',
            contact: this.newProject.contact || '',
            phone: this.newProject.phone || '',
            email: this.newProject.email || '',
            secondaryContact: this.newProject.secondaryContact || '',
            secondaryPhone: this.newProject.secondaryPhone || '',
            secondaryEmail: this.newProject.secondaryEmail || '',
            poNumber: this.newProject.poNumber || '',
            region: this.newProject.region || '',
            callFrequency: this.newProject.callFrequency || '',
            teamLeaders: this.newProject.teamLeaders || [],
            teamLeaderIds: this.newProject.teamLeaderIds || [],
            status: 'ON-CALL',
            contractor: null,
            meters: '',
            mixCode: '',
            requirements: '',
            notes: '',
            created: new Date(),
            updated: new Date(),
          },
        });

        // Add to local array
        this.allProjects.push({ ...payload, id: String(newId) });
        this.allProjects.sort((a, b) =>
          (a.projectName || "").localeCompare(b.projectName || "")
        );

        this.showAddProject = false;
        this.selectProject(payload);
        this.showToast("Project created");
      } catch (e) {
        console.error("Failed to create project:", e);
        this.showToast("Failed to create project");
      } finally {
        this.newProjectSaving = false;
      }
    },

    async duplicateProject() {
      if (!this.selectedProject) return;
      const src = this.selectedProject;
      try {
        let maxId = 0;
        this.allProjects.forEach((p) => {
          const id = parseInt(p.projectId, 10);
          if (!isNaN(id) && id > maxId) maxId = id;
        });
        const newId = maxId + 1;

        const payload = {
          projectId: newId,
          projectName: (src.projectName || '') + ' (Copy)',
          company: src.company || '',
          contact: src.contact || '',
          phone: src.phone || '',
          email: src.email || '',
          formattedAddress: src.formattedAddress || '',
          region: src.region || '',
          poNumber: src.poNumber || '',
          secondaryContact: src.secondaryContact || '',
          secondaryPhone: src.secondaryPhone || '',
          secondaryEmail: src.secondaryEmail || '',
          callFrequency: src.callFrequency || '',
          customerId: src.customerId || '',
          active: true,
          closed: false,
          created: new Date(),
        };

        await setFire({
          collection: "projects",
          doc: String(newId),
          body: payload,
        });

        this.allProjects.push({ ...payload, id: String(newId) });
        this.allProjects.sort((a, b) =>
          (a.projectName || "").localeCompare(b.projectName || "")
        );

        this.selectProject(payload);
        this.$nextTick(() => {
          this.openEditProject();
        });
        this.showToast("Project duplicated — edit details below");
      } catch (e) {
        console.error("Failed to duplicate project:", e);
        this.showToast("Failed to duplicate project");
      }
    },

    showToast(msg) {
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toast = msg;
      this.toastTimer = setTimeout(() => {
        this.toast = "";
      }, 3000);
    },
  },

  async created() {
    // Check legacy preference
    if (localStorage.getItem("jobsViewMode") === "legacy") {
      this.$router.replace({ name: "JobsLegacy" });
      return;
    }
    this.customFieldDefs = await loadDefinitions();
    await this.loadData();

    // Deep-link: if ?jobId= is present, select that job and its project
    const linkedJobId = this.$route.query.jobId;
    if (linkedJobId) {
      this.deepLinkToJob(linkedJobId);
    }

    // Deep-link: if ?projectId= is present, select that project (and optionally open edit modal)
    const linkedProjectId = this.$route.query.projectId;
    if (linkedProjectId) {
      this.deepLinkToProject(linkedProjectId);
    }
  },

  mounted() {
    // Map init will happen when a project is selected
  },

  beforeDestroy() {
    this.stopTesterPolling();
    this.destroyMap();
    if (this.jobsUnsubscribe) {
      this.jobsUnsubscribe();
      this.jobsUnsubscribe = null;
    }
    if (this.unsubTestReports) {
      this.unsubTestReports();
      this.unsubTestReports = null;
    }
  },
};
</script>

<style>
@keyframes enterprise-pulse {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  100% {
    transform: scale(1.8);
    opacity: 0;
  }
}

#enterprise-map,
#enterprise-map-mobile {
  width: 100%;
  height: 100%;
  background: #1a1a2e;
}

.mapboxgl-popup-content {
  padding: 8px 12px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  background: #1e293b;
  color: #e2e8f0;
}

.mapboxgl-popup-tip {
  border-top-color: #1e293b;
}

.mapboxgl-popup-close-button {
  color: #94a3b8;
}

/* iMessage-style chat bubbles */
.imsg-bubble {
  border-radius: 18px;
  word-break: break-word;
  line-height: 1.35;
}
.imsg-sent {
  background: #007aff;
  color: #fff;
  border-bottom-right-radius: 4px;
}
.imsg-recv {
  background: #e5e5ea;
  color: #000;
  border-bottom-left-radius: 4px;
}
</style>
